import { useState, useEffect, useCallback, Fragment } from 'react';
import { clinicAuthAPI, billingAPI } from '../lib/api';
import { printInvoiceDocument, printSalesOrderDocument, invoiceTotals } from '../lib/documentPrint';
import ItemSearchPicker from '../components/ItemSearchPicker';
import { INVOICE_TYPES, coreFieldsError } from '../lib/mandatoryFields';
import { SOCoreFieldsPanel, soCoreError } from '../components/SOCoreFields';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast, Toaster } from 'sonner';
import {
  Receipt, SignOut, Plus, MagnifyingGlass, CurrencyInr,
  CalendarBlank, CreditCard, ArrowRight, Trash, Printer, DownloadSimple,
  ShoppingCart, Users, Money, Eye, Clock, Truck, CheckCircle,
  Package, ArrowsClockwise, Storefront, Bank, Paperclip,
} from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);

const SALUTATIONS = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'M/s.'];
const VISIT_TYPES = ['Clinic Visit', 'Home Visit'];

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Chandigarh",
  "Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
];

const SO_STATUS = {
  confirmed: { label: 'Confirmed', color: 'bg-amber-100 text-amber-700' },
  partially_invoiced: { label: 'Partially Invoiced', color: 'bg-blue-100 text-blue-700' },
  invoiced: { label: 'Invoiced', color: 'bg-emerald-100 text-emerald-700' },
  void: { label: 'Void', color: 'bg-red-100 text-red-700' },
};

const INV_STATUS = {
  paid: { label: 'Paid', color: 'bg-green-50 text-green-700 border-green-200' },
  partial: { label: 'Partially Paid', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  overdue: { label: 'Overdue', color: 'bg-red-50 text-red-700 border-red-200' },
  unpaid: { label: 'Unpaid', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  void: { label: 'Void', color: 'bg-gray-100 text-gray-500 border-gray-200' },
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-500 border-gray-200' },
};
const invStatusMeta = (s) => {
  const key = String(s || '').toLowerCase();
  return INV_STATUS[key] || { label: s || '—', color: 'bg-gray-100 text-gray-600 border-gray-200' };
};


/* ═══════════════════════════════════════════════════════════════════
   MAIN PORTAL
   ═══════════════════════════════════════════════════════════════════ */
export default function ClinicPortalPage() {
  const [clinic, setClinic] = useState(null);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem('clinic_token');
    const c = localStorage.getItem('clinic_data');
    if (t && c) { setToken(t); setClinic(JSON.parse(c)); }
  }, []);

  const handleLogin = (t, c) => {
    localStorage.setItem('clinic_token', t);
    localStorage.setItem('clinic_data', JSON.stringify(c));
    // Clear acting clinic on fresh login
    localStorage.removeItem('acting_clinic_id');
    setToken(t); setClinic(c);
  };

  const handleLogout = () => {
    localStorage.removeItem('clinic_token');
    localStorage.removeItem('clinic_data');
    localStorage.removeItem('acting_clinic_id');
    setToken(null); setClinic(null);
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      {(!token || !clinic) ? <LoginView onLogin={handleLogin} /> : <ClinicDashboard clinic={clinic} onLogout={handleLogout} />}
    </>
  );
}

/* ─── Login ─────────────────────────────────────────────── */
function LoginView({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const { data } = await clinicAuthAPI.emailLogin(email.trim(), password);
      localStorage.setItem('clinic_token', data.token);
      const me = await clinicAuthAPI.getMe();
      onLogin(data.token, me.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="clinic-login-page">
      <div className="w-full max-w-md">
        <Card className="border border-slate-200 shadow-xl rounded-2xl overflow-hidden">
          <div className="h-1.5 w-full bg-[#0F4C81]" />
          <CardHeader className="text-center pb-2 pt-8">
            <img src="/hearclear_logo.png" alt="HearClear" className="h-14 mx-auto mb-3" data-testid="clinic-login-logo" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EBF3FA] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#0F4C81] mx-auto w-fit">Clinic Staff</span>
            <CardTitle className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-[#0B1B3D] mt-3">HearClear Clinic Portal</CardTitle>
            <p className="text-sm text-slate-500">Front-desk billing, patients &amp; inventory</p>
          </CardHeader>
          <CardContent className="pb-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-lg" data-testid="login-error">{error}</div>}
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">Work Email</label>
                <Input data-testid="clinic-email-input" type="email" placeholder="you@hearclear.in" value={email}
                  onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">Password</label>
                <Input data-testid="clinic-password-input" type="password" placeholder="Enter password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-lg" />
              </div>
              <Button data-testid="clinic-login-btn" type="submit" disabled={loading}
                className="w-full bg-[#0F4C81] hover:bg-[#0B3C68] text-white rounded-lg h-11 font-semibold">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-slate-400 mt-6">© {new Date().getFullYear()} HearClear · Powered by Fintexa</p>
      </div>
    </div>
  );
}

/* ─── Dashboard ─────────────────────────────────────────── */
function BranchRequiredNotice() {
  return (
    <Card className="border border-dashed border-gray-300 rounded-2xl" data-testid="branch-required-notice">
      <CardContent className="py-14 text-center">
        <Storefront size={44} className="mx-auto mb-3 text-gray-300" />
        <p className="text-[#0B1B3D] font-medium">Select a branch first</p>
        <p className="text-sm text-gray-400 mt-1">Choose the clinic branch from the selector at the top-right<br />to create an invoice or sales order for it.</p>
      </CardContent>
    </Card>
  );
}


function ClinicDashboard({ clinic, onLogout }) {
  const [tab, setTab] = useState('home');
  const [summary, setSummary] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [audiologists, setAudiologists] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Branch selector — corporate (all clinics) OR a user allocated to >1 clinic.
  const isCorporate = clinic.is_corporate === true;
  const sessionClinics = clinic.available_clinics || [];
  const isMultiClinic = !isCorporate && sessionClinics.length > 1;
  const showSelector = isCorporate || isMultiClinic;
  const [allClinics, setAllClinics] = useState(isMultiClinic ? sessionClinics : []);
  const [actingClinicId, setActingClinicId] = useState(localStorage.getItem('acting_clinic_id') || '');
  const actingClinic = allClinics.find(c => c.id === actingClinicId);
  const perms = clinic.permissions;
  const can = (m, a = 'view') => !perms || (perms[m] || []).includes(a);

  useEffect(() => {
    if (isCorporate) {
      clinicAuthAPI.getAllClinicsBrief().then(res => setAllClinics(res.data.clinics || [])).catch(() => {});
    } else if (isMultiClinic) {
      setAllClinics(sessionClinics);
    }
  }, [isCorporate, isMultiClinic]);

  const handleClinicChange = (id) => {
    setActingClinicId(id);
    if (id) {
      localStorage.setItem('acting_clinic_id', id);
    } else {
      localStorage.removeItem('acting_clinic_id');
    }
    // Reload data after clinic change
    setTimeout(() => loadAll(), 100);
  };

  const loadAll = useCallback(async () => {
    try {
      const [sumRes, invRes, audRes] = await Promise.all([
        billingAPI.getRevenueSummary({}),
        billingAPI.getInvoices({ page_size: 20 }),
        clinicAuthAPI.getAudiologists(),
      ]);
      setSummary(sumRes.data);
      setInvoices(invRes.data.invoices);
      setAudiologists(audRes.data.audiologists);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadInventory = useCallback(async (search = '') => {
    try {
      const res = await clinicAuthAPI.getMyInventory({ search });
      setInventory(res.data.items);
    } catch (e) { console.error(e); }
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FB]" data-testid="clinic-dashboard">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <img src="/hearclear_logo.png" alt="HearClear" className="h-9" data-testid="clinic-header-logo" />
          <div className="h-8 w-px bg-gray-200" />
          <div>
            <h1 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">
              {showSelector ? (actingClinic ? actingClinic.name : (isCorporate ? 'Corporate Office' : 'All My Clinics')) : clinic.name}
            </h1>
            <p className="text-xs text-gray-500 font-['Plus_Jakarta_Sans']">
              {showSelector
                ? (actingClinic ? `Acting as: ${actingClinic.name}` : (isCorporate ? 'All Clinics' : 'Select a branch to create'))
                : `${clinic.state} | Code: ${clinic.code}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {showSelector && (
            <Select value={actingClinicId || '__all__'} onValueChange={v => handleClinicChange(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-[220px] rounded-xl text-sm" data-testid="corporate-clinic-selector">
                <SelectValue placeholder="All Clinics" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{isCorporate ? 'All Clinics (View Only)' : 'All My Clinics (View Only)'}</SelectItem>
                {allClinics.map((c, i) => (
                  <SelectItem key={c.id || c.code || i} value={c.id}>{c.code} — {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" onClick={onLogout} className="text-gray-500 hover:text-red-600" data-testid="clinic-logout-btn">
            <SignOut size={20} className="mr-1" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={tab} onValueChange={setTab} key={actingClinicId || 'all'}>
          <TabsList className="bg-white border border-gray-200 rounded-xl p-1 mb-6 flex-wrap" data-testid="clinic-tabs">
            <TabsTrigger value="home" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">Home</TabsTrigger>
            {can('invoices', 'create') && <TabsTrigger value="new-invoice" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">New Invoice</TabsTrigger>}
            {can('sales_orders', 'create') && <TabsTrigger value="new-so" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">New Sales Order</TabsTrigger>}
            {can('sales_orders') && <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">Sales Orders</TabsTrigger>}
            {can('credit_notes') && <TabsTrigger value="credit-notes" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">Credit Notes</TabsTrigger>}
            {can('customers') && <TabsTrigger value="customers" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">Customers</TabsTrigger>}
            {can('invoices') && <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">Invoices</TabsTrigger>}
            {can('payments') && <TabsTrigger value="cash-ledger" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white" data-testid="clinic-cash-ledger-tab">Cash Ledger</TabsTrigger>}
            {can('inventory') && <TabsTrigger value="inventory" className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white" data-testid="clinic-inventory-tab">Inventory</TabsTrigger>}
          </TabsList>

          <TabsContent value="home">
            <HomeTab summary={summary} invoices={invoices} onNewInvoice={() => setTab('new-invoice')}
              onNewSO={() => setTab('new-so')} onViewInvoice={setSelectedInvoice}
              createDisabled={showSelector && !actingClinicId}
              canCreateInvoice={can('invoices', 'create') && !(showSelector && !actingClinicId)}
              canCreateSO={can('sales_orders', 'create') && !(showSelector && !actingClinicId)} />
          </TabsContent>
          {can('invoices', 'create') && (
          <TabsContent value="new-invoice">
            {showSelector && !actingClinicId
              ? <BranchRequiredNotice />
              : <TransactionForm clinic={actingClinic || clinic} audiologists={audiologists} inventory={inventory}
                  loadInventory={loadInventory} onCreated={() => { setTab('home'); loadAll(); }} mode="invoice" />}
          </TabsContent>
          )}
          {can('sales_orders', 'create') && (
          <TabsContent value="new-so">
            {showSelector && !actingClinicId
              ? <BranchRequiredNotice />
              : <TransactionForm clinic={actingClinic || clinic} audiologists={audiologists} inventory={inventory}
                  loadInventory={loadInventory} onCreated={() => { setTab('orders'); loadAll(); }} mode="sales_order" />}
          </TabsContent>
          )}
          {can('sales_orders') && <TabsContent value="orders"><SalesOrdersTab clinic={actingClinic || clinic} setTab={setTab} canConvert={can('invoices', 'create')} canPay={can('payments', 'create')} /></TabsContent>}
          {can('credit_notes') && <TabsContent value="credit-notes"><CreditNotesTab clinic={actingClinic || clinic} invoices={invoices} /></TabsContent>}
          {can('customers') && <TabsContent value="customers"><CustomersTab clinic={actingClinic || clinic} canCreate={can('customers', 'create')} /></TabsContent>}
          {can('invoices') && <TabsContent value="history"><HistoryTab clinic={actingClinic || clinic} onViewInvoice={setSelectedInvoice} /></TabsContent>}
          {can('payments') && <TabsContent value="cash-ledger"><CashLedgerTab /></TabsContent>}
          {can('inventory') && <TabsContent value="inventory"><InventoryTab clinic={actingClinic || clinic} /></TabsContent>}
        </Tabs>
      </div>

      {selectedInvoice && <InvoiceDetailModal invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} canPay={can('payments', 'create')} />}
    </div>
  );
}

/* ─── Cash Ledger Tab (clinic) ──────────────────────────── */
function InventoryTab({ clinic }) {
  const [data, setData] = useState({ items: [], total_items: 0, total_units: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await billingAPI.getInventoryInHand({ search }); setData(d); }
    catch (e) { /* noop */ }
    setLoading(false);
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-4" data-testid="clinic-inventory-view">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items..." className="pl-9 rounded-xl" data-testid="inventory-search" />
        </div>
        <div className="ml-auto flex gap-3">
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-center">
            <p className="text-[10px] text-gray-500 uppercase">Distinct Items</p>
            <p className="text-lg font-bold text-[#0B1B3D]" data-testid="inventory-total-items">{data.total_items}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-center">
            <p className="text-[10px] text-gray-500 uppercase">Units In Hand</p>
            <p className="text-lg font-bold text-emerald-600" data-testid="inventory-total-units">{data.total_units}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-10 text-gray-400">Loading inventory...</p>
      ) : data.items.length === 0 ? (
        <Card className="border border-dashed border-gray-300 rounded-2xl">
          <CardContent className="py-12 text-center text-gray-400">
            <Truck size={40} className="mx-auto mb-3 text-gray-300" />
            No stock in hand at this clinic
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs">
                <th className="text-left p-3">Item</th>
                <th className="text-right p-3 w-32">In Hand</th>
                <th className="text-right p-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, i) => (
                <Fragment key={it.item_id || i}>
                  <tr className="border-t border-gray-100 hover:bg-gray-50" data-testid={`inventory-row-${i}`}>
                    <td className="p-3 font-medium text-gray-800">{it.item_name}</td>
                    <td className="p-3 text-right"><span className="font-bold text-emerald-600">{it.in_hand}</span></td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-600" onClick={() => setExpanded(expanded === i ? null : i)}>
                        {expanded === i ? 'Hide' : 'Serials'}
                      </Button>
                    </td>
                  </tr>
                  {expanded === i && (
                    <tr className="bg-gray-50/60"><td colSpan={3} className="p-3">
                      <div className="flex flex-wrap gap-1.5">
                        {it.serials.map(sn => <span key={sn} className="text-[10px] bg-white border border-gray-200 rounded-full px-2 py-0.5 font-mono">{sn}</span>)}
                      </div>
                    </td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function CashLedgerTab() {
  const [data, setData] = useState({ entries: [], balance: 0, cash_in: 0, cash_out: 0, total: 0, filtered: false });
  const [loading, setLoading] = useState(true);
  const [showExpense, setShowExpense] = useState(false);
  const [showOpening, setShowOpening] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [form, setForm] = useState({ amount: '', remarks: '', entry_date: new Date().toISOString().split('T')[0], category: '' });
  const [deposit, setDeposit] = useState({ amount: '', entry_date: new Date().toISOString().split('T')[0], destination: 'bank', bank_name: '', reference: '', remarks: '' });
  const [receiptFile, setReceiptFile] = useState(null);
  const [banks, setBanks] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data: d } = await billingAPI.getCashLedger(params);
      setData(d);
    }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { billingAPI.getBanks().then(({ data: d }) => setBanks(d.banks || [])).catch(() => {}); }, []);

  const resetForms = () => {
    setForm({ amount: '', remarks: '', entry_date: new Date().toISOString().split('T')[0], category: '' });
    setDeposit({ amount: '', entry_date: new Date().toISOString().split('T')[0], destination: 'bank', bank_name: '', reference: '', remarks: '' });
    setReceiptFile(null);
    setShowExpense(false); setShowOpening(false); setShowDeposit(false);
  };

  const submitExpense = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      let attachment_path = '', attachment_name = '';
      if (receiptFile) {
        const fd = new FormData();
        fd.append('file', receiptFile);
        const { data: up } = await billingAPI.uploadCashReceipt(fd);
        attachment_path = up.path; attachment_name = up.name;
      }
      await billingAPI.addCashExpense({ ...form, amount: Number(form.amount), attachment_path, attachment_name });
      toast.success('Expense recorded'); resetForms(); load();
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const submitOpening = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try { await billingAPI.setCashOpening({ ...form, amount: Number(form.amount) }); toast.success('Cash added'); resetForms(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const submitDeposit = async () => {
    if (!deposit.amount || Number(deposit.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (deposit.destination === 'bank' && !deposit.bank_name) { toast.error('Select the bank'); return; }
    setSaving(true);
    try { await billingAPI.depositCash({ ...deposit, amount: Number(deposit.amount) }); toast.success('Deposit recorded'); resetForms(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="cash-ledger-tab">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs text-emerald-700 mb-1">Cash In{data.filtered ? ' (period)' : ''}</p>
          <p className="text-xl font-bold text-emerald-700">₹{fmt(data.cash_in)}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-xs text-red-700 mb-1">Cash Out{data.filtered ? ' (period)' : ''}</p>
          <p className="text-xl font-bold text-red-700">₹{fmt(data.cash_out)}</p>
        </div>
        <div className="bg-[#0F4C81] text-white rounded-xl p-4" data-testid="cash-in-hand-card">
          <p className="text-xs text-white/70 mb-1">Cash In Hand (current)</p>
          <p className="text-xl font-bold">₹{fmt(data.balance)}</p>
        </div>
      </div>

      {/* Date range filter */}
      <div className="flex flex-wrap items-end gap-2 bg-gray-50 border rounded-xl p-3" data-testid="cash-date-filter">
        <div><label className="text-[11px] text-gray-500 block">From</label>
          <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-lg h-9 w-40" data-testid="cash-from-date" /></div>
        <div><label className="text-[11px] text-gray-500 block">To</label>
          <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-lg h-9 w-40" data-testid="cash-to-date" /></div>
        {(fromDate || toDate) && (
          <Button size="sm" variant="ghost" className="h-9 text-xs" onClick={() => { setFromDate(''); setToDate(''); }} data-testid="cash-clear-filter">Clear</Button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button size="sm" onClick={() => { resetForms(); setShowExpense(true); }} className="rounded-xl bg-red-600 hover:bg-red-700 text-white" data-testid="add-expense-btn"><Money size={14} className="mr-1" /> Record Expense</Button>
        <Button size="sm" onClick={() => { resetForms(); setShowDeposit(true); }} className="rounded-xl bg-[#0F4C81] hover:bg-[#0B3C68] text-white" data-testid="deposit-cash-btn"><Bank size={14} className="mr-1" /> Deposit Cash</Button>
        <Button size="sm" variant="outline" onClick={() => { resetForms(); setShowOpening(true); }} className="rounded-xl" data-testid="add-cash-btn"><Plus size={14} className="mr-1" /> Add Cash / Opening</Button>
      </div>

      {(showExpense || showOpening) && (
        <div className="border rounded-xl p-4 space-y-3 bg-gray-50" data-testid="cash-entry-form">
          <p className="text-sm font-semibold">{showExpense ? 'Record Cash Expense' : 'Add Cash / Opening Balance'}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-gray-500">Amount *</label><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="rounded-lg h-9" data-testid="cash-amount-input" /></div>
            <div><label className="text-xs text-gray-500">Date</label><Input type="date" value={form.entry_date} onChange={e => setForm(f => ({ ...f, entry_date: e.target.value }))} className="rounded-lg h-9" data-testid="cash-date-input" /></div>
            <div><label className="text-xs text-gray-500">Remarks</label><Input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} placeholder={showExpense ? 'e.g. Office supplies' : 'e.g. Opening balance'} className="rounded-lg h-9" data-testid="cash-remarks-input" /></div>
            {showExpense && (
              <div className="sm:col-span-3">
                <label className="text-xs text-gray-500 flex items-center gap-1"><Paperclip size={12} /> Receipt (image / PDF)</label>
                <Input type="file" accept="image/*,application/pdf" onChange={e => setReceiptFile(e.target.files?.[0] || null)} className="rounded-lg h-9 text-xs" data-testid="cash-receipt-input" />
                {receiptFile && <p className="text-[11px] text-gray-500 mt-1">{receiptFile.name}</p>}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={showExpense ? submitExpense : submitOpening} disabled={saving} className="rounded-xl bg-[#0F4C81] text-white" data-testid="cash-save-btn">{saving ? 'Saving...' : 'Save'}</Button>
            <Button size="sm" variant="ghost" onClick={resetForms}>Cancel</Button>
          </div>
        </div>
      )}

      {showDeposit && (
        <div className="border rounded-xl p-4 space-y-3 bg-gray-50" data-testid="cash-deposit-form">
          <p className="text-sm font-semibold">Deposit Cash (reduces cash in hand)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-gray-500">Amount *</label><Input type="number" value={deposit.amount} onChange={e => setDeposit(d => ({ ...d, amount: e.target.value }))} className="rounded-lg h-9" data-testid="deposit-amount-input" /></div>
            <div><label className="text-xs text-gray-500">Date</label><Input type="date" value={deposit.entry_date} onChange={e => setDeposit(d => ({ ...d, entry_date: e.target.value }))} className="rounded-lg h-9" data-testid="deposit-date-input" /></div>
            <div>
              <label className="text-xs text-gray-500">Transferred To *</label>
              <select value={deposit.destination} onChange={e => setDeposit(d => ({ ...d, destination: e.target.value, bank_name: '' }))}
                className="w-full h-9 border border-gray-200 rounded-lg px-2 text-sm" data-testid="deposit-destination-select">
                <option value="bank">Bank</option>
                <option value="head_office">Head Office</option>
              </select>
            </div>
            {deposit.destination === 'bank' && (
              <div><label className="text-xs text-gray-500">Bank Name *</label>
                <select value={deposit.bank_name} onChange={e => setDeposit(d => ({ ...d, bank_name: e.target.value }))}
                  className="w-full h-9 border border-gray-200 rounded-lg px-2 text-sm" data-testid="deposit-bank-select">
                  <option value="">Select bank</option>
                  {banks.map(b => <option key={b.id} value={b.account_name}>{b.account_name}</option>)}
                </select>
              </div>
            )}
            <div><label className="text-xs text-gray-500">Reference / Slip No.</label><Input value={deposit.reference} onChange={e => setDeposit(d => ({ ...d, reference: e.target.value }))} placeholder="Deposit slip / UTR" className="rounded-lg h-9" data-testid="deposit-reference-input" /></div>
            <div><label className="text-xs text-gray-500">Remarks</label><Input value={deposit.remarks} onChange={e => setDeposit(d => ({ ...d, remarks: e.target.value }))} className="rounded-lg h-9" data-testid="deposit-remarks-input" /></div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={submitDeposit} disabled={saving} className="rounded-xl bg-[#0F4C81] text-white" data-testid="deposit-save-btn">{saving ? 'Saving...' : 'Save Deposit'}</Button>
            <Button size="sm" variant="ghost" onClick={resetForms}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="cash-ledger-table">
          <thead><tr className="bg-gray-50 text-gray-500 text-xs"><th className="text-left p-3">Date</th><th className="text-left p-3">Type</th><th className="text-left p-3">Source</th><th className="text-left p-3">Remarks / Reference</th><th className="text-center p-3">Receipt</th><th className="text-right p-3">Amount</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0B1B3D] border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : data.entries.length > 0 ? data.entries.map(e => (
              <tr key={e.id} className="border-t hover:bg-gray-50">
                <td className="p-3 text-gray-600">{e.entry_date}</td>
                <td className="p-3"><span className={`px-2 py-0.5 text-xs rounded ${e.type === 'in' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{e.type === 'in' ? 'Cash In' : 'Cash Out'}</span></td>
                <td className="p-3 text-gray-500 capitalize">{(e.source || '').replace(/_/g, ' ')}</td>
                <td className="p-3 text-gray-700">{e.remarks || '-'}{e.ref_number ? <span className="text-xs text-gray-400 ml-1 font-mono">{e.ref_number}</span> : null}</td>
                <td className="p-3 text-center">
                  {e.attachment_path ? (
                    <button onClick={() => billingAPI.viewCashReceipt(e.attachment_path)} className="text-[#0F4C81] hover:underline inline-flex items-center gap-1 text-xs" data-testid={`view-receipt-${e.id}`}><Paperclip size={12} /> View</button>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className={`p-3 text-right font-medium ${e.type === 'in' ? 'text-emerald-700' : 'text-red-700'}`}>{e.type === 'in' ? '+' : '−'}₹{fmt(e.amount)}</td>
              </tr>
            )) : (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">No cash entries for the selected period.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Home Tab ──────────────────────────────────────────── */
function HomeTab({ summary, invoices, onNewInvoice, onNewSO, onViewInvoice, canCreateInvoice, canCreateSO, createDisabled }) {
  const months = summary.recent_months || [];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Today's Revenue" value={`₹${fmt(summary.today_revenue)}`} sub={`${summary.today_count || 0} invoices`} icon={CurrencyInr} color="emerald" />
        {months.map((m, i) => (
          <StatCard
            key={m.month}
            label={i === 0 ? `This Month (${m.label})` : m.label}
            value={`₹${fmt(m.revenue)}`}
            sub={`${m.count || 0} invoices`}
            icon={Receipt}
            color={i === 0 ? 'blue' : 'purple'}
            data-testid={`clinic-month-revenue-${i}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <StatCard label="This Month GST" value={`₹${fmt(summary.month_tax)}`} sub="Collected this month" icon={CalendarBlank} color="amber" />
        <StatCard label="This Month Cash / Card / UPI" value={`₹${fmt(summary.month_cash)}`}
          sub={`Card: ₹${fmt(summary.month_card)} | UPI: ₹${fmt(summary.month_upi)}`} icon={CreditCard} color="blue" />
      </div>

      {createDisabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
          Select a specific clinic from the dropdown above to create invoices, sales orders, or credit notes.
        </div>
      )}

      {(canCreateInvoice || canCreateSO) && (
      <Card className="border border-gray-200 rounded-2xl">
        <CardContent className="p-5 flex gap-3 flex-wrap">
          {canCreateInvoice && (
          <Button data-testid="create-invoice-btn" onClick={onNewInvoice}
            className="bg-[#0F4C81] hover:bg-[#0B3C68] text-white rounded-xl h-12 px-6 text-base">
            <Plus size={20} className="mr-2" /> Create Invoice
          </Button>
          )}
          {canCreateSO && (
          <Button data-testid="create-so-btn" onClick={onNewSO} variant="outline"
            className="rounded-xl h-12 px-6 text-base border-[#0B1B3D] text-[#0B1B3D]">
            <ShoppingCart size={20} className="mr-2" /> Create Sales Order
          </Button>
          )}
        </CardContent>
      </Card>
      )}

      <Card className="border border-gray-200 rounded-2xl">
        <CardHeader className="pb-3"><CardTitle className="text-lg font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">Recent Invoices</CardTitle></CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-gray-400 text-sm py-6 text-center">No invoices yet</p>
          ) : (
            <div className="space-y-2">
              {invoices.slice(0, 10).map((inv) => (
                <div key={inv.id} onClick={() => onViewInvoice(inv)} data-testid={`invoice-row-${inv.id}`}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 cursor-pointer border border-gray-100">
                  <div>
                    <p className="font-medium text-sm text-[#0B1B3D]">{inv.invoice_number}</p>
                    <p className="text-xs text-gray-500">{inv.customer?.name || inv.customer_name || '—'} | {inv.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm text-[#0B1B3D]">₹{fmt(inv.grand_total)}</p>
                    <Badge variant="outline" className={`text-xs ${invStatusMeta(inv.payment_status).color}`}>
                      {invStatusMeta(inv.payment_status).label}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color, ...rest }) {
  const colors = { emerald: 'bg-emerald-50 text-emerald-700', blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700', purple: 'bg-purple-50 text-purple-700' };
  return (
    <Card className="border border-gray-200 rounded-2xl" {...rest}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}><Icon size={18} weight="bold" /></div>
          <span className="text-xs text-gray-500 font-['Plus_Jakarta_Sans']">{label}</span>
        </div>
        <p className="text-xl font-semibold font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">{value}</p>
        <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
      </CardContent>
    </Card>
  );
}

/* ─── Serial dropdown (pick-from-stock only, no manual entry) ─── */
const PAYMENT_MODE_FALLBACK = [
  { code: 'cash', label: 'Cash' }, { code: 'card', label: 'Card' },
  { code: 'upi', label: 'UPI' }, { code: 'bank_transfer', label: 'Bank Transfer' },
];
function usePaymentModes() {
  const [modes, setModes] = useState(PAYMENT_MODE_FALLBACK);
  useEffect(() => {
    billingAPI.getPaymentModes().then(({ data }) => {
      if (data.payment_modes?.length) setModes(data.payment_modes);
    }).catch(() => {});
  }, []);
  return modes;
}

function SerialSelect({ itemId, itemName, location, value, onChange, testid }) {
  const [serials, setSerials] = useState([]);
  useEffect(() => {
    let active = true;
    if (!itemId && !itemName) { setSerials([]); return; }
    billingAPI.getAvailableSerials({ item_id: itemId || '', item_name: itemName || '', location: location || '' })
      .then(({ data }) => { if (active) setSerials(data.serial_numbers || []); })
      .catch(() => { if (active) setSerials([]); });
    return () => { active = false; };
  }, [itemId, itemName, location]);
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      className="w-full h-8 border border-gray-200 rounded-lg px-2 text-xs bg-white focus:border-[#0B1B3D] focus:ring-1 focus:ring-[#0B1B3D]"
      data-testid={testid}>
      <option value="">{serials.length ? 'Select serial…' : 'No stock serials'}</option>
      {value && !serials.includes(value) && <option value={value}>{value}</option>}
      {serials.map(sn => <option key={sn} value={sn}>{sn}</option>)}
    </select>
  );
}

/* ─── Multi serial picker: pick up to `max` (= quantity) serials from stock ─── */
function SerialMultiSelect({ itemId, itemName, location, value = [], max = 1, onChange, testid }) {
  const [serials, setSerials] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let active = true;
    if (!itemId && !itemName) { setSerials([]); return; }
    billingAPI.getAvailableSerials({ item_id: itemId || '', item_name: itemName || '', location: location || '' })
      .then(({ data }) => { if (active) setSerials(data.serial_numbers || []); })
      .catch(() => { if (active) setSerials([]); });
    return () => { active = false; };
  }, [itemId, itemName, location]);

  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const options = [...new Set([...selected, ...serials])];
  const cap = Math.max(1, Number(max) || 1);

  const toggle = (sn) => {
    const isSel = selected.includes(sn);
    if (isSel) onChange(selected.filter(s => s !== sn));
    else if (selected.length < cap) onChange([...selected, sn]);
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="w-full h-8 border border-gray-200 rounded-lg px-2 text-xs bg-white text-left flex items-center justify-between focus:border-[#0B1B3D]"
        data-testid={testid}>
        <span className={selected.length ? 'text-[#0B1B3D] truncate' : 'text-gray-400'}>
          {selected.length ? `${selected.length}/${cap} selected` : (options.length ? 'Select serial…' : 'No stock serials')}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" className={`transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}><path d="M1 1l4 4 4-4" stroke="#64748B" strokeWidth="1.5" fill="none" /></svg>
      </button>
      {open && options.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-44 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {options.map(sn => {
            const checked = selected.includes(sn);
            const disabled = !checked && selected.length >= cap;
            return (
              <label key={sn} className={`flex items-center gap-2 px-2 py-1.5 text-xs ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'} ${checked ? 'bg-emerald-50' : ''}`}>
                <input type="checkbox" checked={checked} disabled={disabled}
                  onMouseDown={e => { e.preventDefault(); if (!disabled) toggle(sn); }}
                  onChange={() => {}} className="h-3.5 w-3.5" />
                <span className="font-mono">{sn}</span>
              </label>
            );
          })}
        </div>
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selected.map(sn => (
            <span key={sn} className="inline-flex items-center gap-0.5 text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-mono">
              {sn}
              <button type="button" onMouseDown={e => { e.preventDefault(); toggle(sn); }} className="text-gray-400 hover:text-red-500 ml-0.5">&times;</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TRANSACTION FORM (Invoice or Sales Order)
   ═══════════════════════════════════════════════════════════════════ */
function TransactionForm({ clinic, audiologists, inventory, loadInventory, onCreated, mode }) {
  const isInvoice = mode === 'invoice';
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_address: '', customer_state: clinic.state || '',
    customer_gstin: '',
    audiologist_employee_number: '', audiologist_name: '',
    second_audiologist_employee_number: '', second_audiologist_name: '',
    lead_source: '', visit_type: '', invoice_type: '',
    doc_date: new Date().toISOString().split('T')[0],
    discount_amount: 0, discount_type: 'fixed', notes: isInvoice ? 'Thank you for choosing HearClear Hearing Clinics!' : '',
    payment_mode: 'cash', payment_status: 'paid', deposit_to: '', amount_paid: '', expected_delivery_date: '',
  });
  const [banks, setBanks] = useState([]);
  useEffect(() => { billingAPI.getBanks().then(({ data }) => setBanks(data.banks || [])).catch(() => {}); }, []);
  const payModes = usePaymentModes();
  const [lineItems, setLineItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [showCustDrop, setShowCustDrop] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [docNumber, setDocNumber] = useState('');
  const [useManualNumber, setUseManualNumber] = useState(false);
  const [manualNumber, setManualNumber] = useState('');
  const [custCredit, setCustCredit] = useState(0);
  const [creditCustomerId, setCreditCustomerId] = useState('');
  const [applyCreditAmt, setApplyCreditAmt] = useState('');

  // Load lead sources + the auto-generated next document number preview
  useEffect(() => {
    clinicAuthAPI.getLeadSources().then(r => setLeadSources(r.data.lead_sources || [])).catch(() => {});
    const fetchNum = isInvoice ? clinicAuthAPI.getNextInvoiceNumber() : clinicAuthAPI.getNextSONumber();
    fetchNum.then(r => setDocNumber(r.data.next_invoice_number || r.data.next_so_number || '')).catch(() => {});
  }, [isInvoice]);

  // Debounced customer search (name/phone) so staff can find & select an existing patient
  useEffect(() => {
    const q = (form.customer_name || '').trim();
    if (q.length < 2) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await billingAPI.getCustomers({ search: q });
        setCustResults(data.customers || []);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [form.customer_name]);

  const selectCustomer = (c) => {
    setForm(f => ({
      ...f,
      customer_name: c.name || '',
      customer_phone: c.phone || f.customer_phone,
      customer_address: (typeof c.address === 'string' ? c.address : '') || f.customer_address,
      customer_state: c.place_of_supply || c.state || f.customer_state,
      customer_gstin: c.gstin || f.customer_gstin || '',
    }));
    setShowCustDrop(false);
    setCustResults([]);
    // Fetch the patient's available advance/credit so it can be adjusted on this invoice
    setCustCredit(0); setCreditCustomerId(''); setApplyCreditAmt('');
    if (c.id && isInvoice) {
      billingAPI.getCustomerCreditBalance(c.id)
        .then(({ data }) => { setCustCredit(data.credit_balance || 0); setCreditCustomerId(c.id); })
        .catch(() => {});
    }
  };

  useEffect(() => { loadInventory(''); }, [loadInventory]);

  const addLineItem = (item) => {
    setLineItems(prev => [...prev, {
      item_id: item.id || '', name: item.name, description: item.description || '',
      serial_number: '', serial_numbers: [], hsn_sac: item.hsn_or_sac || '', quantity: 1,
      rate: item.rate || 0, discount_percent: 0, discount_amount: 0, _discountType: 'percent',
      tax_percent: item.tax_percentage || 0,
    }]);
    setShowItemPicker(false); setItemSearch('');
  };

  const updateLineItem = (idx, field, value) => setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  const removeLineItem = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const calcLine = (li) => {
    const amt = li.quantity * li.rate;
    const disc = li._discountType === 'amount'
      ? Math.min(Number(li.discount_amount) || 0, amt)
      : amt * ((Number(li.discount_percent) || 0) / 100);
    const taxable = amt - disc;
    const tax = taxable * (li.tax_percent / 100);
    return { amt, disc, taxable, tax, total: taxable + tax };
  };

  const totals = lineItems.reduce((acc, li) => {
    const c = calcLine(li);
    const isInter = form.customer_state && clinic.state && form.customer_state.toLowerCase() !== clinic.state.toLowerCase();
    return {
      subtotal: acc.subtotal + c.amt, discount: acc.discount + c.disc, taxable: acc.taxable + c.taxable,
      cgst: acc.cgst + (isInter ? 0 : c.tax / 2), sgst: acc.sgst + (isInter ? 0 : c.tax / 2),
      igst: acc.igst + (isInter ? c.tax : 0), total: acc.total + c.total,
    };
  }, { subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 });

  const discountValue = form.discount_type === 'percent'
    ? Math.round(totals.total * (form.discount_amount || 0) / 100 * 100) / 100
    : (form.discount_amount || 0);
  const grandTotal = Math.max(totals.total - discountValue, 0);
  const appliedCredit = isInvoice ? Math.min(Number(applyCreditAmt) || 0, custCredit || 0, grandTotal) : 0;

  const handleSubmit = async () => {
    const coreErr = coreFieldsError({ name: form.customer_name, phone: form.customer_phone, leadSource: form.lead_source,
      audiologist: form.audiologist_employee_number || form.audiologist_name, invoiceType: form.invoice_type });
    if (coreErr) { toast.error(coreErr); return setError(coreErr); }
    if (lineItems.length === 0) { toast.error('Add at least one item'); return setError('Add at least one item'); }
    setError(''); setSubmitting(true);
    try {
      const cleanItems = lineItems.map(li => ({
        item_id: li.item_id || '', name: li.name, description: li.description || '',
        serial_number: li.serial_number || '', serial_numbers: li.serial_numbers || [],
        hsn_sac: li.hsn_sac || '', quantity: li.quantity, rate: li.rate,
        discount_percent: li._discountType === 'amount' ? 0 : (Number(li.discount_percent) || 0),
        discount_amount: li._discountType === 'amount' ? (Number(li.discount_amount) || 0) : 0,
        tax_percent: li.tax_percent,
      }));
      const base = { ...form, line_items: cleanItems, discount_amount: discountValue };
      if (isInvoice) {
        await billingAPI.createInvoice({
          ...base,
          amount_paid: Number(form.amount_paid) || 0,
          invoice_date: form.doc_date,
          invoice_number_manual: useManualNumber ? manualNumber : '',
          customer_id: creditCustomerId || '',
          apply_credit_amount: appliedCredit || 0,
        });
        toast.success('Invoice created!');
      } else {
        await billingAPI.createSalesOrder({
          ...base,
          so_date: form.doc_date,
          expected_shipping_date: form.expected_delivery_date,
          so_number_manual: useManualNumber ? manualNumber : '',
        });
        toast.success('Sales Order created and sent!');
      }
      onCreated();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const filteredItems = inventory.filter(i =>
    i.name?.toLowerCase().includes(itemSearch.toLowerCase()) || i.sku?.toLowerCase().includes(itemSearch.toLowerCase())
  ).slice(0, 20);

  const setAudiologist = (empNo) => {
    const aud = audiologists.find(a => a.employee_number === empNo);
    setForm(f => ({ ...f, audiologist_employee_number: empNo, audiologist_name: aud?.full_name || aud?.display_name || '' }));
  };

  const setSecondAudiologist = (empNo) => {
    const aud = audiologists.find(a => a.employee_number === empNo);
    setForm(f => ({ ...f, second_audiologist_employee_number: empNo, second_audiologist_name: aud?.full_name || aud?.display_name || '' }));
  };

  return (
    <div className="space-y-6" data-testid={isInvoice ? 'new-invoice-form' : 'new-so-form'}>
      <h2 className="text-xl font-semibold font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">
        {isInvoice ? 'Create Invoice' : 'Create Sales Order'}
      </h2>
      {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-xl" data-testid="form-error">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Customer */}
          <Card className="border border-gray-200 rounded-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">Customer Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="relative">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Name *</label>
                <Input data-testid="customer-name" placeholder="Search or type patient name" value={form.customer_name}
                  onChange={e => { setForm(f => ({ ...f, customer_name: e.target.value })); setShowCustDrop(true); }}
                  onFocus={() => custResults.length && setShowCustDrop(true)}
                  onBlur={() => setTimeout(() => setShowCustDrop(false), 150)}
                  autoComplete="off" className="rounded-xl" />
                {showCustDrop && custResults.length > 0 && (
                  <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto" data-testid="customer-search-dropdown">
                    {custResults.map((c, i) => (
                      <div key={c.id || i} onMouseDown={() => selectCustomer(c)}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-50 last:border-0"
                        data-testid={`customer-option-${i}`}>
                        <span className="font-medium text-[#0B1B3D]">{c.name}</span>
                        {c.phone && <span className="text-gray-400 ml-2 text-xs">{c.phone}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Phone <span className="text-red-500">*</span></label>
                <Input data-testid="customer-phone" placeholder="Mobile number" value={form.customer_phone}
                  onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} className="rounded-xl" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Address</label>
                <Input data-testid="customer-address" placeholder="Full address" value={form.customer_address}
                  onChange={e => setForm(f => ({ ...f, customer_address: e.target.value }))} className="rounded-xl" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">State</label>
                <Select value={form.customer_state} onValueChange={v => setForm(f => ({ ...f, customer_state: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="customer-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">GSTIN</label>
                <Input data-testid="customer-gstin" placeholder="Customer GSTIN (optional)" value={form.customer_gstin}
                  onChange={e => setForm(f => ({ ...f, customer_gstin: e.target.value }))} className="rounded-xl" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Audiologist <span className="text-red-500">*</span></label>
                <Select value={form.audiologist_employee_number} onValueChange={setAudiologist}>
                  <SelectTrigger className="rounded-xl" data-testid="audiologist-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {audiologists.map((a, i) => (
                      <SelectItem key={`${a.employee_number}-${i}`} value={a.employee_number}>
                        {a.full_name || a.display_name} — {a.job_title || a.department}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Details: number, date, audiologists, lead source, visit type */}
          <Card className="border border-gray-200 rounded-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">{isInvoice ? 'Invoice' : 'Sales Order'} Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600 block">{isInvoice ? 'Invoice' : 'SO'} Number</label>
                  <button type="button" className="text-[10px] text-blue-600 underline" data-testid="toggle-manual-number"
                    onClick={() => setUseManualNumber(v => !v)}>{useManualNumber ? 'Auto-generate' : 'Manual entry'}</button>
                </div>
                {useManualNumber ? (
                  <Input data-testid="doc-number-manual" placeholder="Enter number" value={manualNumber}
                    onChange={e => setManualNumber(e.target.value)} className="rounded-xl" />
                ) : (
                  <Input data-testid="doc-number-auto" value={docNumber} readOnly
                    className="rounded-xl bg-gray-50 text-gray-600" />
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">{isInvoice ? 'Invoice' : 'SO'} Date</label>
                <Input type="date" data-testid="doc-date" value={form.doc_date}
                  onChange={e => setForm(f => ({ ...f, doc_date: e.target.value }))} className="rounded-xl" />
              </div>
              {!isInvoice && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Expected Shipping Date</label>
                  <Input type="date" data-testid="expected-shipping-date" value={form.expected_delivery_date}
                    onChange={e => setForm(f => ({ ...f, expected_delivery_date: e.target.value }))} className="rounded-xl" />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Second Audiologist</label>
                <Select value={form.second_audiologist_employee_number} onValueChange={setSecondAudiologist}>
                  <SelectTrigger className="rounded-xl" data-testid="second-audiologist-select"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                  <SelectContent>
                    {audiologists.map((a, i) => (
                      <SelectItem key={`sec-${a.employee_number}-${i}`} value={a.employee_number}>{a.full_name || a.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Lead Source <span className="text-red-500">*</span></label>
                <Select value={form.lead_source} onValueChange={v => setForm(f => ({ ...f, lead_source: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="lead-source-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {leadSources.map(ls => <SelectItem key={ls.id || ls.name} value={ls.name}>{ls.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Visit Type</label>
                <Select value={form.visit_type} onValueChange={v => setForm(f => ({ ...f, visit_type: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="visit-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {VISIT_TYPES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Invoice Type <span className="text-red-500">*</span></label>
                <Select value={form.invoice_type} onValueChange={v => setForm(f => ({ ...f, invoice_type: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="invoice-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {INVOICE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card className="border border-gray-200 rounded-2xl">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">Items</CardTitle>
              <Button variant="outline" size="sm" onClick={() => { setShowItemPicker(true); loadInventory(''); }}
                className="rounded-xl" data-testid="add-item-btn"><Plus size={16} className="mr-1" /> Add Item</Button>
            </CardHeader>
            <CardContent>
              {showItemPicker && (
                <div className="mb-4 border border-gray-200 rounded-xl p-3 bg-gray-50">
                  <div className="relative mb-2">
                    <MagnifyingGlass size={16} className="absolute left-3 top-3 text-gray-400" />
                    <Input data-testid="item-search" placeholder="Search items..." value={itemSearch}
                      onChange={e => { setItemSearch(e.target.value); loadInventory(e.target.value); }} className="pl-9 rounded-xl" autoFocus />
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {filteredItems.map(item => (
                      <div key={item.id} onClick={() => addLineItem(item)}
                        className="flex justify-between items-center p-2 rounded-lg hover:bg-white cursor-pointer text-sm">
                        <div><span className="font-medium text-[#0B1B3D]">{item.name}</span><Badge variant="outline" className="ml-2 text-xs">{item.category}</Badge></div>
                        <span className="text-gray-600">₹{fmt(item.rate)}</span>
                      </div>
                    ))}
                    {filteredItems.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No items found</p>}
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <Button variant="ghost" size="sm" onClick={() => addLineItem({ name: '', rate: 0, tax_percentage: 0 })} className="text-xs">
                      <Plus size={14} className="mr-1" /> Add custom item
                    </Button>
                  </div>
                </div>
              )}
              {lineItems.length === 0 ? (
                <p className="text-gray-400 text-sm py-8 text-center">No items added yet</p>
              ) : (
                <div className="space-y-3">
                  {lineItems.map((li, idx) => (
                    <div key={idx} className="border border-gray-100 rounded-xl p-3" data-testid={`line-item-${idx}`}>
                      <div className="flex items-start justify-between mb-2">
                        <Input value={li.name} onChange={e => updateLineItem(idx, 'name', e.target.value)}
                          placeholder="Item name" className="rounded-lg text-sm font-medium flex-1 mr-2" />
                        <Button variant="ghost" size="sm" onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-600 shrink-0"><Trash size={16} /></Button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {isInvoice && (
                          <div><label className="text-[10px] text-gray-500">Serial No. {li.quantity > 1 ? `(${li.quantity})` : ''}</label>
                            <SerialMultiSelect itemId={li.item_id} itemName={li.name} location={clinic.name}
                              value={li.serial_numbers || []} max={li.quantity} onChange={vals => updateLineItem(idx, 'serial_numbers', vals)}
                              testid={`line-serial-${idx}`} /></div>
                        )}
                        <div><label className="text-[10px] text-gray-500">Qty</label>
                          <Input type="number" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', Number(e.target.value))} className="rounded-lg text-xs h-8" min="1" /></div>
                        <div><label className="text-[10px] text-gray-500">Rate (₹)</label>
                          <Input type="number" value={li.rate} onChange={e => updateLineItem(idx, 'rate', Number(e.target.value))} className="rounded-lg text-xs h-8" /></div>
                        <div>
                          <div className="flex items-center justify-between">
                            <label className="text-[10px] text-gray-500">Discount</label>
                            <div className="flex bg-gray-100 rounded p-0.5">
                              <button type="button" onClick={() => updateLineItem(idx, '_discountType', 'percent')}
                                className={`px-1.5 rounded text-[10px] font-medium ${li._discountType !== 'amount' ? 'bg-white text-[#0B1B3D] shadow-sm' : 'text-gray-500'}`} data-testid={`line-disc-type-pct-${idx}`}>%</button>
                              <button type="button" onClick={() => updateLineItem(idx, '_discountType', 'amount')}
                                className={`px-1.5 rounded text-[10px] font-medium ${li._discountType === 'amount' ? 'bg-white text-[#0B1B3D] shadow-sm' : 'text-gray-500'}`} data-testid={`line-disc-type-amt-${idx}`}>₹</button>
                            </div>
                          </div>
                          {li._discountType === 'amount' ? (
                            <Input type="number" value={li.discount_amount} onChange={e => updateLineItem(idx, 'discount_amount', Number(e.target.value))} className="rounded-lg text-xs h-8" min="0" placeholder="₹ amount" data-testid={`line-disc-amt-${idx}`} />
                          ) : (
                            <Input type="number" value={li.discount_percent} onChange={e => updateLineItem(idx, 'discount_percent', Number(e.target.value))} className="rounded-lg text-xs h-8" min="0" max="100" placeholder="0-100%" data-testid={`line-disc-pct-${idx}`} />
                          )}
                        </div>
                        <div><label className="text-[10px] text-gray-500">GST %</label>
                          <Input type="number" value={li.tax_percent} onChange={e => updateLineItem(idx, 'tax_percent', Number(e.target.value))} className="rounded-lg text-xs h-8" min="0" /></div>
                      </div>
                      <div className="mt-2 flex justify-between text-xs text-gray-500">
                        <span>HSN: {li.hsn_sac || '—'}</span>
                        <span className="font-medium text-[#0B1B3D]">₹{fmt(calcLine(li).total)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment / Delivery Options */}
          <Card className="border border-gray-200 rounded-2xl">
            <CardContent className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {isInvoice ? (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Payment Mode</label>
                    <Select value={form.payment_mode} onValueChange={v => setForm(f => ({ ...f, payment_mode: v }))}>
                      <SelectTrigger className="rounded-xl" data-testid="payment-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {payModes.map(m => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Payment Status</label>
                    <Select value={form.payment_status} onValueChange={v => setForm(f => ({ ...f, payment_status: v }))}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">Paid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.payment_status === 'partial' && (
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Amount Paid *</label>
                      <Input type="number" min="0" value={form.amount_paid}
                        onChange={e => setForm(f => ({ ...f, amount_paid: e.target.value }))}
                        placeholder="Enter amount received" className="rounded-xl" data-testid="new-invoice-amount-paid" />
                    </div>
                  )}
                  {form.payment_status !== 'unpaid' && (
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Deposit To</label>
                      <select value={form.deposit_to} onChange={e => setForm(f => ({ ...f, deposit_to: e.target.value }))}
                        className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm focus:border-[#0B1B3D] focus:ring-1 focus:ring-[#0B1B3D]" data-testid="new-invoice-deposit-to">
                        <option value="">Select account</option>
                        {banks.map(b => <option key={b.id} value={b.account_name}>{b.account_name}</option>)}
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Expected Delivery</label>
                  <Input type="date" value={form.expected_delivery_date}
                    onChange={e => setForm(f => ({ ...f, expected_delivery_date: e.target.value }))} className="rounded-xl" data-testid="expected-delivery" />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Additional Discount</label>
                <div className="flex gap-1">
                  <div className="flex bg-gray-100 rounded-lg p-0.5">
                    <button type="button" onClick={() => setForm(f => ({ ...f, discount_type: 'fixed' }))}
                      className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${form.discount_type === 'fixed' ? 'bg-white text-[#0B1B3D] shadow-sm' : 'text-gray-500'}`}>₹</button>
                    <button type="button" onClick={() => setForm(f => ({ ...f, discount_type: 'percent' }))}
                      className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${form.discount_type === 'percent' ? 'bg-white text-[#0B1B3D] shadow-sm' : 'text-gray-500'}`}>%</button>
                  </div>
                  <Input data-testid="additional-discount" type="number" value={form.discount_amount}
                    onChange={e => setForm(f => ({ ...f, discount_amount: Number(e.target.value) }))} className="rounded-xl flex-1" min="0"
                    placeholder={form.discount_type === 'percent' ? '0-100%' : '₹ amount'} />
                </div>
                {form.discount_type === 'percent' && form.discount_amount > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1">= ₹{fmt(discountValue)}</p>
                )}
              </div>
              <div className={isInvoice ? "sm:col-span-3" : "sm:col-span-2"}>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
                <textarea data-testid="form-notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:border-[#0B1B3D] focus:ring-1 focus:ring-[#0B1B3D] resize-none" rows={2} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary */}
        <div className="space-y-5">
          <Card className="border border-gray-200 rounded-2xl sticky top-24">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">
              {isInvoice ? 'Invoice' : 'Sales Order'} Summary
            </CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>₹{fmt(totals.subtotal)}</span></div>
                {totals.discount > 0 && <div className="flex justify-between text-orange-600"><span>Line Discounts</span><span>-₹{fmt(totals.discount)}</span></div>}
                <div className="flex justify-between text-gray-500"><span>Taxable Amount</span><span>₹{fmt(totals.taxable)}</span></div>
                {totals.cgst > 0 && <div className="flex justify-between text-gray-500"><span>CGST</span><span>₹{fmt(totals.cgst)}</span></div>}
                {totals.sgst > 0 && <div className="flex justify-between text-gray-500"><span>SGST</span><span>₹{fmt(totals.sgst)}</span></div>}
                {totals.igst > 0 && <div className="flex justify-between text-gray-500"><span>IGST</span><span>₹{fmt(totals.igst)}</span></div>}
                {(form.discount_amount || 0) > 0 && <div className="flex justify-between text-orange-600"><span>Add. Discount {form.discount_type === 'percent' ? `(${form.discount_amount}%)` : ''}</span><span>-₹{fmt(discountValue)}</span></div>}
              </div>
              <div className="border-t pt-3 flex justify-between font-semibold text-lg text-[#0B1B3D]">
                <span>Grand Total</span><span>₹{fmt(grandTotal)}</span>
              </div>
              {isInvoice && custCredit > 0 && (
                <div className="border-t pt-3 space-y-2 bg-emerald-50/50 -mx-1 px-2 rounded-lg" data-testid="advance-credit-block">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-emerald-700 font-medium">Advance / Credit available</span>
                    <span className="text-emerald-700 font-semibold" data-testid="advance-credit-available">₹{fmt(custCredit)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 whitespace-nowrap">Adjust</label>
                    <Input type="number" min="0" value={applyCreditAmt}
                      onChange={e => setApplyCreditAmt(e.target.value)}
                      placeholder="₹ to adjust" className="rounded-lg h-8 text-xs" data-testid="apply-credit-input" />
                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs rounded-lg whitespace-nowrap"
                      onClick={() => setApplyCreditAmt(String(Math.min(custCredit, grandTotal)))} data-testid="apply-credit-max-btn">Max</Button>
                  </div>
                  {appliedCredit > 0 && (
                    <>
                      <div className="flex justify-between text-emerald-700 text-sm"><span>Advance Applied</span><span data-testid="advance-applied-amount">-₹{fmt(appliedCredit)}</span></div>
                      <div className="flex justify-between font-semibold text-[#0B1B3D]"><span>Outstanding After Credit</span><span data-testid="outstanding-after-credit">₹{fmt(Math.max(grandTotal - appliedCredit, 0))}</span></div>
                    </>
                  )}
                </div>
              )}
              <Button data-testid="submit-form-btn" onClick={handleSubmit} disabled={submitting || lineItems.length === 0}
                className="w-full bg-[#0F4C81] hover:bg-[#0B3C68] text-white rounded-xl h-12 mt-4 text-base">
                {submitting ? 'Creating...' : isInvoice ? <>Create Invoice <ArrowRight size={18} className="ml-2" /></> : <>Create & Send Order <ArrowRight size={18} className="ml-2" /></>}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SALES ORDERS TAB (Clinic view)
   ═══════════════════════════════════════════════════════════════════ */
function SalesOrdersTab({ clinic, setTab, canConvert = true, canPay = true }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedSO, setSelectedSO] = useState(null);
  const [convertDialog, setConvertDialog] = useState(null);
  const [editSO, setEditSO] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 50 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await billingAPI.getSalesOrders(params);
      setOrders(data.sales_orders || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter, search, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="sales-orders-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-xl" data-testid="so-search" />
        </div>
        <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-xl w-40" data-testid="so-from-date" />
        <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-xl w-40" data-testid="so-to-date" />
        <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 rounded-xl" data-testid="so-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {Object.entries(SO_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="rounded-xl"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
      </div>

      <p className="text-sm text-gray-500">{total} orders</p>

      <div className="space-y-2">
        {loading ? <p className="text-center py-8 text-gray-400">Loading...</p> :
          orders.length === 0 ? <p className="text-center py-8 text-gray-400">No sales orders</p> :
          orders.map(so => (
            <Card key={so.id} className="border border-gray-100 rounded-xl hover:border-gray-300 cursor-pointer" data-testid={`so-card-${so.id}`}>
              <CardContent className="p-4 flex items-center justify-between" onClick={() => setSelectedSO(so)}>
                <div>
                  <p className="font-medium text-sm text-[#0B1B3D]">{so.so_number}</p>
                  <p className="text-xs text-gray-500">{so.customer?.name} | {so.date}</p>
                  <p className="text-xs text-gray-400">{so.line_items?.length || 0} items</p>
                </div>
                <div className="text-right flex flex-col items-end gap-1">
                  <p className="font-semibold text-[#0B1B3D]">₹{fmt(so.grand_total)}</p>
                  <Badge variant="outline" className={`text-xs ${SO_STATUS[so.status]?.color || ''}`}>{SO_STATUS[so.status]?.label || so.status}</Badge>
                  {!so.converted_to_invoice_id && so.status !== 'cancelled' && so.status !== 'invoiced' && (
                    <Button size="sm" variant="outline" className="text-xs h-6 rounded-lg text-[#0F4C81] border-[#BFDBFE] mt-1"
                      onClick={e => { e.stopPropagation(); setEditSO(so); }} data-testid={`edit-so-${so.id}`}>
                      Edit
                    </Button>
                  )}
                  {canConvert && !so.converted_to_invoice_id && so.status !== 'cancelled' && so.status !== 'invoiced' && (
                    <Button size="sm" variant="outline" className="text-xs h-6 rounded-lg text-blue-600 border-blue-200 mt-1"
                      onClick={e => { e.stopPropagation(); setConvertDialog(so); }} data-testid={`convert-so-${so.id}`}>
                      Convert to Invoice
                    </Button>
                  )}
                  {so.converted_to_invoice_number && (
                    <span className="text-xs text-green-600">Inv: {so.converted_to_invoice_number}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        }
      </div>

      {selectedSO && <SODetailDialog so={selectedSO} onClose={() => setSelectedSO(null)} onRefresh={load} canPay={canPay} />}
      {convertDialog && <ConvertSODialog so={convertDialog} clinic={clinic} onClose={() => setConvertDialog(null)} onConverted={() => { setConvertDialog(null); load(); setTab('history'); }} />}
      {editSO && <EditSODialog so={editSO} onClose={() => setEditSO(null)} onSaved={() => { setEditSO(null); load(); }} />}
    </div>
  );
}

/* ─── Edit Sales Order (clinic) ─────────────────────────── */
function EditSODialog({ so, onClose, onSaved }) {
  const [customerName, setCustomerName] = useState(so.customer?.name || so.customer_name || '');
  const [customerPhone, setCustomerPhone] = useState(so.customer?.phone || so.customer_phone || '');
  const [notes, setNotes] = useState(so.notes || '');
  const [items, setItems] = useState(() => (so.line_items || []).map(li => ({
    item_id: li.item_id || '', name: li.name || '', hsn_sac: li.hsn_sac || '',
    quantity: li.quantity || 1, rate: li.rate || 0,
    discount_amount: li.discount_amount || li.discount || 0,
    discount_percent: li.discount_percent || 0,
    tax_percent: li.tax_percent || 0,
  })));
  const [submitting, setSubmitting] = useState(false);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    clinicAuthAPI.getMyInventory({ search: '' }).then(r => setCatalog(r.data.items || [])).catch(() => setCatalog([]));
  }, []);

  const upd = (idx, field, value) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const pickItem = (idx, item) => setItems(prev => prev.map((it, i) => i === idx ? {
    ...it, item_id: item.id || '', name: item.name, hsn_sac: item.hsn_or_sac || item.hsn_sac || '',
    rate: item.rate || it.rate || 0, tax_percent: item.tax_percentage ?? it.tax_percent ?? 0,
  } : it));
  const addRow = () => setItems(prev => [...prev, { item_id: '', name: '', hsn_sac: '', quantity: 1, rate: 0, discount_amount: 0, discount_percent: 0, tax_percent: 0 }]);
  const removeRow = (idx) => setItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  const lineTotal = (it) => Math.max((Number(it.quantity) || 0) * (Number(it.rate) || 0) - (Number(it.discount_amount) || 0), 0);
  const grand = items.reduce((s, it) => s + lineTotal(it) * (1 + (Number(it.tax_percent) || 0) / 100), 0);

  const save = async () => {
    if (!customerName.trim()) { toast.error('Customer name is required'); return; }
    if (!items.some(it => it.name)) { toast.error('At least one item is required'); return; }
    setSubmitting(true);
    try {
      await billingAPI.editSalesOrder(so.id, {
        customer_name: customerName,
        customer_phone: customerPhone,
        notes,
        line_items: items.filter(it => it.name).map(it => ({
          item_id: it.item_id, name: it.name, hsn_sac: it.hsn_sac,
          quantity: Number(it.quantity) || 1, rate: Number(it.rate) || 0,
          discount_amount: Number(it.discount_amount) || 0,
          discount_percent: Number(it.discount_percent) || 0,
          tax_percent: Number(it.tax_percent) || 0,
        })),
      });
      toast.success(`Sales Order ${so.so_number} updated`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update Sales Order'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!so} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="edit-so-dialog">
        <DialogHeader><DialogTitle>Edit {so.so_number}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Customer Name</label>
              <Input value={customerName} onChange={e => setCustomerName(e.target.value)} data-testid="edit-so-customer-name" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Customer Phone</label>
              <Input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} data-testid="edit-so-customer-phone" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs text-gray-500">Line Items</label>
            <div className="grid grid-cols-12 gap-2 px-2 text-[10px] uppercase tracking-wide text-gray-400">
              <span className="col-span-4">Item</span><span className="col-span-1">Qty</span><span className="col-span-2">Rate</span>
              <span className="col-span-2">Disc ₹</span><span className="col-span-1">Tax%</span><span className="col-span-1 text-right">Total</span><span className="col-span-1" />
            </div>
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center border rounded-lg p-2" data-testid={`edit-so-line-${idx}`}>
                <div className="col-span-4"><ItemSearchPicker items={catalog} value={it.name} onPick={(item) => pickItem(idx, item)} onType={(v) => upd(idx, 'name', v)} testid={`edit-so-item-name-${idx}`} placeholder="Search item..." /></div>
                <Input className="col-span-1 h-8 text-xs px-1" type="number" min="1" value={it.quantity} onChange={e => upd(idx, 'quantity', e.target.value)} data-testid={`edit-so-item-qty-${idx}`} />
                <Input className="col-span-2 h-8 text-xs" type="number" value={it.rate} onChange={e => upd(idx, 'rate', e.target.value)} data-testid={`edit-so-item-rate-${idx}`} />
                <Input className="col-span-2 h-8 text-xs" type="number" value={it.discount_amount} onChange={e => upd(idx, 'discount_amount', e.target.value)} data-testid={`edit-so-item-disc-${idx}`} />
                <Input className="col-span-1 h-8 text-xs px-1" type="number" value={it.tax_percent} onChange={e => upd(idx, 'tax_percent', e.target.value)} data-testid={`edit-so-item-tax-${idx}`} />
                <span className="col-span-1 text-[10px] text-gray-500 text-right">₹{fmt(lineTotal(it))}</span>
                <Button variant="ghost" size="sm" className="col-span-1 h-7 w-7 p-0 text-red-400 hover:text-red-600" disabled={items.length <= 1}
                  onClick={() => removeRow(idx)} data-testid={`edit-so-item-remove-${idx}`}><Trash size={14} /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addRow} data-testid="edit-so-add-item"><Plus size={14} className="mr-1" /> Add Item</Button>
          </div>
          <div>
            <label className="text-xs text-gray-500">Notes</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} data-testid="edit-so-notes" />
          </div>
          <p className="text-right text-sm font-semibold text-[#0B1B3D]">Estimated Total: ₹{fmt(grand)}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-so-cancel">Cancel</Button>
          <Button onClick={save} disabled={submitting} className="bg-[#0F4C81] text-white" data-testid="edit-so-save">
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── SO Detail Dialog ──────────────────────────────────── */
function SODetailDialog({ so, onClose, onRefresh, canPay = true }) {
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'cash', payment_date: new Date().toISOString().split('T')[0], notes: '', deposit_to: '' });
  const [payments, setPayments] = useState([]);
  const [showPayForm, setShowPayForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [banks, setBanks] = useState([]);
  const payModes = usePaymentModes();
  useEffect(() => { billingAPI.getBanks().then(({ data }) => setBanks(data.banks || [])).catch(() => {}); }, []);

  const STATUS_TRANSITIONS = {
    sent: [{ value: 'placed', label: 'Mark as Placed' }, { value: 'cancelled', label: 'Cancel Order' }],
    placed: [{ value: 'in_transit', label: 'Mark as In Transit' }, { value: 'cancelled', label: 'Cancel Order' }],
    in_transit: [{ value: 'fulfilled', label: 'Mark as Fulfilled' }, { value: 'cancelled', label: 'Cancel Order' }],
  };

  const nextActions = STATUS_TRANSITIONS[so.status] || [];

  const loadPayments = useCallback(async () => {
    try {
      const { data } = await billingAPI.getPayments({ so_id: so.id });
      setPayments(data.payments || []);
    } catch (e) { console.error(e); }
  }, [so.id]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const handleRecordPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) return;
    if (!payForm.notes || !payForm.notes.trim()) { toast.error('Remark is required'); return; }
    setSubmitting(true);
    try {
      await billingAPI.recordPayment({ so_id: so.id }, { ...payForm, amount: Number(payForm.amount) });
      toast.success('Payment recorded');
      setPayForm({ amount: '', payment_mode: 'cash', payment_date: new Date().toISOString().split('T')[0], notes: '', deposit_to: '' });
      setShowPayForm(false);
      loadPayments();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
  const balance = Math.max(so.grand_total - totalPaid, 0);

  const handlePrintSO = async () => {
    try {
      const { data } = await billingAPI.getSalesOrder(so.id);
      printSalesOrderDocument(data, { payments: data.payments || payments });
    } catch (e) { printSalesOrderDocument(so, { payments }); }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const clinicInfo = JSON.parse(localStorage.getItem('hearclear_clinic') || '{}');
      await billingAPI.updateSOStatus(so.id, { status: newStatus, note: statusNote || `Status changed to ${newStatus}`, updated_by: clinicInfo.name || clinicInfo.code || '' });
      toast.success(`Status updated to ${newStatus}`);
      setStatusNote('');
      onRefresh();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update status'); }
    finally { setUpdatingStatus(false); }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="so-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {so.so_number}
            <Badge variant="outline" className={`text-xs ${SO_STATUS[so.status]?.color || ''}`}>{SO_STATUS[so.status]?.label || so.status}</Badge>
            <Button variant="outline" size="sm" onClick={handlePrintSO} className="ml-auto rounded-xl gap-1.5" data-testid="print-so-btn"><Printer size={15} /> Print</Button>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-400">Customer</p>
            <p className="font-medium">{so.customer?.name}</p>
            <p className="text-gray-500">{so.customer?.phone}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Date: {so.date}</p>
            <p className="font-semibold text-lg text-[#0B1B3D]">₹{fmt(so.grand_total)}</p>
          </div>
        </div>

        <div className="mt-3">
          <h4 className="text-sm font-semibold text-[#0B1B3D] mb-2">Items</h4>
          {so.line_items?.map((li, i) => (
            <div key={i} className="flex justify-between text-sm border-b border-gray-50 py-1">
              <span>{li.name} x{li.quantity}</span>
              <span className="font-medium">₹{fmt(li.total)}</span>
            </div>
          ))}
        </div>

        {/* Status Change Actions */}
        {nextActions.length > 0 && (
          <div className="mt-4 p-3 border rounded-xl bg-blue-50/50">
            <h4 className="text-xs font-semibold text-[#0B1B3D] mb-2">Change Status</h4>
            <div className="flex flex-wrap gap-2">
              {nextActions.map(a => (
                <Button key={a.value} size="sm"
                  variant={a.value === 'cancelled' ? 'destructive' : 'default'}
                  className={a.value !== 'cancelled' ? 'bg-[#0F4C81] text-white rounded-xl' : 'rounded-xl'}
                  disabled={updatingStatus}
                  onClick={() => handleStatusChange(a.value)}
                  data-testid={`so-status-${a.value}`}>
                  {updatingStatus ? 'Updating...' : a.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Payment Summary */}
        <div className="mt-4 p-3 bg-gray-50 rounded-xl">
          <div className="flex justify-between text-sm"><span className="text-gray-500">Grand Total</span><span className="font-semibold">₹{fmt(so.grand_total)}</span></div>
          <div className="flex justify-between text-sm text-green-600"><span>Total Paid</span><span>₹{fmt(totalPaid)}</span></div>
          <div className="flex justify-between text-sm font-semibold border-t mt-1 pt-1">
            <span className={balance > 0 ? 'text-red-600' : 'text-green-600'}>Balance Due</span>
            <span className={balance > 0 ? 'text-red-600' : 'text-green-600'}>₹{fmt(balance)}</span>
          </div>
        </div>

        {/* Payments */}
        {payments.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-semibold text-[#0B1B3D] mb-2">Payments</h4>
            {payments.map(p => (
              <div key={p.id} className="flex justify-between items-center text-sm py-1 border-b border-gray-50">
                <div><span className="font-medium">₹{fmt(p.amount)}</span> <span className="text-gray-400 text-xs">({p.payment_mode})</span></div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{p.payment_date}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600"
                    onClick={() => billingAPI.getPaymentReceiptPdf(p.id).catch(() => toast.error('Failed'))}
                    data-testid={`receipt-pdf-${p.id}`}>
                    <Printer size={12} className="mr-1" /> Receipt
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Record Payment */}
        {balance > 0 && canPay && (
          <div className="mt-3">
            {!showPayForm ? (
              <Button variant="outline" size="sm" onClick={() => setShowPayForm(true)} className="rounded-xl" data-testid="record-payment-btn">
                <Money size={14} className="mr-1" /> Record Payment
              </Button>
            ) : (
              <div className="border rounded-xl p-3 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div><label className="text-xs text-gray-500">Amount *</label>
                    <Input type="number" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder={`Max ₹${fmt(balance)}`} className="rounded-lg text-sm h-8" data-testid="payment-amount" /></div>
                  <div><label className="text-xs text-gray-500">Mode</label>
                    <Select value={payForm.payment_mode} onValueChange={v => setPayForm(f => ({ ...f, payment_mode: v }))}>
                      <SelectTrigger className="rounded-lg h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {payModes.map(m => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><label className="text-xs text-gray-500">Date</label>
                    <Input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} className="rounded-lg text-xs h-8" /></div>
                  <div className="col-span-3"><label className="text-xs text-gray-500">Deposit To</label>
                    <select value={payForm.deposit_to} onChange={e => setPayForm(f => ({ ...f, deposit_to: e.target.value }))} className="w-full h-8 border rounded-lg px-2 text-xs" data-testid="so-payment-deposit-to">
                      <option value="">Select account</option>
                      {banks.map(b => <option key={b.id} value={b.account_name}>{b.account_name}</option>)}
                    </select></div>
                  <div className="col-span-3"><label className="text-xs text-gray-500">Remark *</label>
                    <Input value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} placeholder="Payment remark (required)" className="rounded-lg text-xs h-8" data-testid="so-payment-remark" /></div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleRecordPayment} disabled={submitting} className="rounded-xl bg-[#0F4C81] text-white" data-testid="confirm-payment-btn">
                    {submitting ? 'Saving...' : 'Save Payment'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowPayForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Status History */}
        {so.status_history?.length > 0 && (
          <div className="mt-3">
            <h4 className="text-xs font-semibold text-gray-500 mb-1">Status History</h4>
            {so.status_history.map((h, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                <Badge variant="outline" className={`text-[10px] ${SO_STATUS[h.status]?.color || ''}`}>{SO_STATUS[h.status]?.label || h.status}</Badge>
                <span>{new Date(h.timestamp).toLocaleString('en-IN')}</span>
                {h.note && <span>— {h.note}</span>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Convert SO → Invoice Dialog ───────────────────────── */
function ConvertSODialog({ so, clinic, onClose, onConverted }) {
  const [lineItems, setLineItems] = useState(
    (so.line_items || []).map(li => ({ ...li, serial_number: '', serial_numbers: [] }))
  );
  const [submitting, setSubmitting] = useState(false);
  const [fill, setFill] = useState({});

  const updateSN = (idx, vals) => setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, serial_numbers: vals } : li));

  const handleConvert = async () => {
    const err = soCoreError(so, fill);
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await billingAPI.convertSOToInvoice(so.id, { line_items: lineItems, notes: so.notes || '', ...fill });
      toast.success('Sales Order converted to Invoice!');
      onConverted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Conversion failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="convert-so-dialog">
        <DialogHeader>
          <DialogTitle>Convert {so.so_number} to Invoice</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500">Select serial numbers from available stock, then confirm. You can record the payment later on the invoice.</p>
        <SOCoreFieldsPanel so={so} fill={fill} setFill={setFill}
          loadAudiologists={clinicAuthAPI.getAudiologists} loadLeadSources={clinicAuthAPI.getLeadSources} />

        <div className="space-y-3 mt-3">
          {lineItems.map((li, idx) => (
            <div key={idx} className="border rounded-xl p-3 flex items-center gap-3">
              <div className="flex-1">
                <p className="font-medium text-sm">{li.name}</p>
                <p className="text-xs text-gray-400">Qty: {li.quantity} | Rate: ₹{fmt(li.rate)} | Total: ₹{fmt(li.total)}</p>
              </div>
              <div className="w-48">
                <label className="text-[10px] text-gray-500">Serial Number{li.quantity > 1 ? `s (${li.quantity})` : ''}</label>
                <SerialMultiSelect itemId={li.item_id} itemName={li.name} location={clinic?.name}
                  value={li.serial_numbers || []} max={li.quantity} onChange={vals => updateSN(idx, vals)}
                  testid={`convert-sn-${idx}`} />
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConvert} disabled={submitting} className="bg-[#0F4C81] text-white rounded-xl" data-testid="confirm-convert-btn">
            {submitting ? 'Converting...' : 'Convert to Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CREDIT NOTES TAB
   ═══════════════════════════════════════════════════════════════════ */
function CreditNotesTab({ clinic, invoices }) {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [total, setTotal] = useState(0);
  const [selectedCN, setSelectedCN] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, page_size: 100 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await billingAPI.getCreditNotes(params);
      setCreditNotes(data.credit_notes || []);
      setTotal(data.total || (data.credit_notes || []).length);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="credit-notes-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search credit notes..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 rounded-xl" data-testid="cn-search" />
        </div>
        <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-xl w-40" data-testid="cn-from-date" />
        <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-xl w-40" data-testid="cn-to-date" />
        <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full" data-testid="cn-viewonly-note">View only · Credit Notes are raised from the main portal</span>
      </div>
      <p className="text-sm text-gray-500">{total} credit notes</p>

      {loading ? <p className="text-center py-8 text-gray-400">Loading...</p> :
        creditNotes.length === 0 ? (
          <Card className="border border-dashed border-gray-300 rounded-2xl">
            <CardContent className="py-12 text-center">
              <Receipt size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-400">No credit notes yet</p>
              <p className="text-xs text-gray-400 mt-1">Credit Notes are raised from the main (admin) portal</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="cn-list">
            {creditNotes.map(cn => {
              const customerName = cn.customer?.name || cn.customer_name || '-';
              return (
                <Card key={cn.id} className="border border-gray-100 rounded-xl hover:border-gray-300 cursor-pointer" data-testid={`cn-card-${cn.cn_number}`}>
                  <CardContent className="p-4 flex items-center justify-between" onClick={() => setSelectedCN(cn)}>
                    <div>
                      <p className="font-medium text-sm text-[#0B1B3D]">{cn.cn_number}</p>
                      <p className="text-xs text-gray-500" data-testid={`cn-customer-${cn.cn_number}`}>{customerName} | {cn.date}</p>
                      <p className="text-xs text-gray-400">
                        {cn.linked_invoice_number ? `Invoice: ${cn.linked_invoice_number}` : 'Standalone'}
                        {cn.line_items?.length ? ` · ${cn.line_items.length} items` : ''}
                      </p>
                    </div>
                    <div className="text-right flex flex-col items-end gap-1">
                      <p className="font-semibold text-red-600">-₹{fmt(cn.grand_total)}</p>
                      {cn.reason && <Badge variant="outline" className="text-xs text-[#64748B] max-w-[160px] truncate">{cn.reason}</Badge>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      {selectedCN && (
        <Dialog open onOpenChange={o => { if (!o) setSelectedCN(null); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="cn-detail-dialog">
            <DialogHeader><DialogTitle>{selectedCN.cn_number}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-gray-400">Customer</p><p className="font-medium">{selectedCN.customer?.name || selectedCN.customer_name || '-'}</p></div>
              <div><p className="text-xs text-gray-400">Date</p><p>{selectedCN.date}</p></div>
              <div><p className="text-xs text-gray-400">Linked Invoice</p><p>{selectedCN.linked_invoice_number || 'Standalone'}</p></div>
              <div><p className="text-xs text-gray-400">Reason</p><p>{selectedCN.reason || '-'}</p></div>
            </div>
            <div className="mt-3 space-y-1">
              {selectedCN.line_items?.map((li, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50">
                  <span>{li.name} x{li.quantity}</span>
                  <span className="font-medium">₹{fmt(li.total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-red-50 rounded-xl text-right">
              <p className="text-xs text-gray-500">Credit Note Total</p>
              <p className="text-xl font-bold text-red-700">₹{fmt(selectedCN.grand_total)}</p>
            </div>
            <Button
              onClick={() => billingAPI.viewCreditNotePdf(selectedCN.id).catch(() => toast.error('Failed to open CN PDF'))}
              className="mt-3 w-full rounded-xl bg-[#0F4C81] text-white hover:bg-[#162E5A]"
              data-testid="clinic-cn-download-pdf-btn"
            >
              <DownloadSimple size={16} className="mr-2" /> Download PDF
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ─── Create Credit Note Dialog ─────────────────────────── */
function CreateCreditNoteDialog({ clinic, invoices, onClose, onCreated }) {
  const [form, setForm] = useState({
    invoice_id: '', customer_name: '', customer_phone: '', reason: '', notes: '',
  });
  const [items, setItems] = useState([{ name: '', quantity: 1, rate: 0, tax_percent: 18 }]);
  const [submitting, setSubmitting] = useState(false);

  const linkInvoice = (invId) => {
    if (!invId || invId === 'standalone') { setForm(f => ({ ...f, invoice_id: '', customer_name: '', customer_phone: '' })); return; }
    const inv = invoices.find(i => i.id === invId);
    if (inv) {
      setForm(f => ({ ...f, invoice_id: invId, customer_name: inv.customer?.name || '', customer_phone: inv.customer?.phone || '' }));
      setItems(inv.line_items?.map(li => ({ name: li.name, quantity: li.quantity, rate: li.rate, tax_percent: li.tax_percent || 18 })) || items);
    }
  };

  const addItem = () => setItems(p => [...p, { name: '', quantity: 1, rate: 0, tax_percent: 18 }]);
  const removeItem = (idx) => setItems(p => p.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => setItems(p => p.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const subtotal = items.reduce((s, li) => s + li.quantity * li.rate, 0);
  const totalTax = items.reduce((s, li) => s + li.quantity * li.rate * (li.tax_percent / 100), 0);
  const grandTotal = subtotal + totalTax;

  const handleCreate = async () => {
    if (!form.customer_name) { toast.error('Customer name is required'); return; }
    if (!items.some(li => li.name && li.rate > 0)) { toast.error('Add at least one item'); return; }
    setSubmitting(true);
    try {
      const payload = {
        invoice_id: form.invoice_id || '',
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        reason: form.reason,
        notes: form.notes,
        line_items: items.filter(li => li.name && li.rate > 0),
      };
      await billingAPI.createCreditNote(payload);
      toast.success('Credit Note created!');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create credit note'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-cn-dialog">
        <DialogHeader><DialogTitle>Create Credit Note</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Link to Invoice (Optional)</label>
            <Select value={form.invoice_id || "standalone"} onValueChange={linkInvoice}>
              <SelectTrigger className="rounded-xl" data-testid="cn-link-invoice">
                <SelectValue placeholder="Select an invoice or leave blank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standalone">Standalone Credit Note</SelectItem>
                {invoices.map(inv => (
                  <SelectItem key={inv.id} value={inv.id}>{inv.invoice_number} — {inv.customer?.name} — ₹{fmt(inv.grand_total)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Customer Name *</label>
              <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                placeholder="Customer name" className="rounded-xl" data-testid="cn-customer-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Customer Phone</label>
              <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                placeholder="Phone" className="rounded-xl" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Reason *</label>
            <Input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="e.g. Defective product return, Pricing error" className="rounded-xl" data-testid="cn-reason" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-[#0B1B3D]">Line Items</label>
              <Button variant="outline" size="sm" onClick={addItem} className="rounded-lg text-xs h-7" data-testid="cn-add-item">
                <Plus size={12} className="mr-1" /> Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((li, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end border rounded-xl p-2">
                  <div className="col-span-4">
                    <label className="text-[10px] text-gray-500">Item Name</label>
                    <Input value={li.name} onChange={e => updateItem(idx, 'name', e.target.value)}
                      placeholder="Item" className="rounded-lg text-sm h-8" data-testid={`cn-item-name-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500">Qty</label>
                    <Input type="number" value={li.quantity} onChange={e => updateItem(idx, 'quantity', Number(e.target.value))}
                      className="rounded-lg text-sm h-8" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500">Rate (₹)</label>
                    <Input type="number" value={li.rate} onChange={e => updateItem(idx, 'rate', Number(e.target.value))}
                      className="rounded-lg text-sm h-8" data-testid={`cn-item-rate-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500">GST %</label>
                    <Input type="number" value={li.tax_percent} onChange={e => updateItem(idx, 'tax_percent', Number(e.target.value))}
                      className="rounded-lg text-sm h-8" />
                  </div>
                  <div className="col-span-1 text-right text-sm font-medium pt-4">
                    ₹{fmt(li.quantity * li.rate * (1 + li.tax_percent / 100))}
                  </div>
                  <div className="col-span-1 pt-4">
                    {items.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-red-500">
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-red-50 rounded-xl p-3 text-right">
            <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span>₹{fmt(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">GST</span><span>₹{fmt(totalTax)}</span></div>
            <div className="flex justify-between text-base font-bold border-t mt-1 pt-1">
              <span className="text-red-700">Credit Note Total</span>
              <span className="text-red-700">₹{fmt(grandTotal)}</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Additional notes" className="rounded-xl" />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={submitting} className="bg-red-700 hover:bg-red-800 text-white rounded-xl" data-testid="confirm-cn-btn">
            {submitting ? 'Creating...' : 'Create Credit Note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CUSTOMERS TAB
   ═══════════════════════════════════════════════════════════════════ */
function CustomersTab({ clinic, canCreate = true }) {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyCust = {
    customer_type: 'individual', salutation: 'Mr.', first_name: '', last_name: '',
    email: '', phone: '', address: '', state: clinic?.state || '',
    gstin: '', place_of_supply: '', business_legal_name: '', business_trade_name: '', tax_preference: 'taxable',
  };
  const [newCust, setNewCust] = useState(emptyCust);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await billingAPI.getCustomers({ search, page_size: 100 });
      setCustomers(data.customers || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newCust.first_name.trim()) { toast.error('First name is required'); return; }
    setCreating(true);
    try {
      const sal = newCust.salutation || '';
      const name = `${sal} ${newCust.first_name} ${newCust.last_name}`.trim();
      await billingAPI.createCustomer({ ...newCust, name });
      toast.success('Customer created');
      setShowCreate(false);
      setNewCust(emptyCust);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create customer'); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search customers by name or phone..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-xl" data-testid="customer-search" />
        </div>
        <Button onClick={() => setShowCreate(true)} className={`rounded-xl bg-[#0F4C81] text-white hover:bg-[#0B3C68] ${canCreate ? '' : 'hidden'}`} data-testid="create-customer-btn">
          <Plus size={16} className="mr-1" /> Create Customer
        </Button>
      </div>

      {loading ? <p className="text-center py-8 text-gray-400">Loading...</p> :
        customers.length === 0 ? <p className="text-center py-8 text-gray-400">No customers yet. Create an invoice or sales order to add customers.</p> :
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {customers.map(c => (
            <Card key={c.id} className="border border-gray-100 rounded-xl hover:border-gray-300 cursor-pointer"
              onClick={() => setSelectedCustomer(c)} data-testid={`customer-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0F4C81] text-white flex items-center justify-center font-bold text-sm">
                    {(c.name || 'C')[0]}
                  </div>
                  <div>
                    <p className="font-medium text-[#0B1B3D]">{c.name}</p>
                    <p className="text-xs text-gray-500">{c.phone || 'No phone'}</p>
                    <p className="text-xs text-gray-400">{c.state}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      }

      {selectedCustomer && <CustomerProfileDialog customerId={selectedCustomer.id} onClose={() => setSelectedCustomer(null)} />}

      {showCreate && (
        <Dialog open onOpenChange={o => { if (!o) setShowCreate(false); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-customer-dialog">
            <DialogHeader><DialogTitle>Create Customer</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              {/* Type */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Customer Type *</label>
                <div className="flex gap-4">
                  {[['individual', 'Individual'], ['business', 'Business']].map(([v, l]) => (
                    <label key={v} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="clinic_cust_type" checked={newCust.customer_type === v}
                        onChange={() => setNewCust(c => ({ ...c, customer_type: v }))} className="accent-[#0B1B3D]" data-testid={`new-ctype-${v}`} />
                      <span className="text-sm">{l}</span>
                    </label>
                  ))}
                </div>
              </div>
              {/* Name */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Salutation</label>
                  <Select value={newCust.salutation} onValueChange={v => setNewCust(c => ({ ...c, salutation: v }))}>
                    <SelectTrigger className="rounded-xl" data-testid="new-customer-salutation"><SelectValue placeholder="--" /></SelectTrigger>
                    <SelectContent>{SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">First Name *</label>
                  <Input value={newCust.first_name} onChange={e => setNewCust(c => ({ ...c, first_name: e.target.value }))} className="rounded-xl" placeholder="First name" data-testid="new-customer-first-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Last Name</label>
                  <Input value={newCust.last_name} onChange={e => setNewCust(c => ({ ...c, last_name: e.target.value }))} className="rounded-xl" placeholder="Last name" data-testid="new-customer-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Email</label>
                  <Input type="email" value={newCust.email} onChange={e => setNewCust(c => ({ ...c, email: e.target.value }))} className="rounded-xl" placeholder="email@example.com" data-testid="new-customer-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label>
                  <Input value={newCust.phone} onChange={e => setNewCust(c => ({ ...c, phone: e.target.value }))} className="rounded-xl" placeholder="+91 XXXXXXXXXX" data-testid="new-customer-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Address</label>
                  <Input value={newCust.address} onChange={e => setNewCust(c => ({ ...c, address: e.target.value }))} className="rounded-xl" placeholder="Full address" data-testid="new-customer-address" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">State</label>
                  <Select value={newCust.state} onValueChange={v => setNewCust(c => ({ ...c, state: v }))}>
                    <SelectTrigger className="rounded-xl" data-testid="new-customer-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent>{INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {/* Business fields */}
              {newCust.customer_type === 'business' && (
                <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-testid="new-customer-business-section">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Business Details</p>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">GSTIN</label>
                    <Input value={newCust.gstin} onChange={e => setNewCust(c => ({ ...c, gstin: e.target.value }))} className="rounded-xl" placeholder="22AAAAA0000A1Z5" data-testid="new-customer-gstin" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Place of Supply</label>
                    <Select value={newCust.place_of_supply} onValueChange={v => setNewCust(c => ({ ...c, place_of_supply: v }))}>
                      <SelectTrigger className="rounded-xl" data-testid="new-customer-supply"><SelectValue placeholder="Select state..." /></SelectTrigger>
                      <SelectContent>{INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Business Legal Name</label>
                      <Input value={newCust.business_legal_name} onChange={e => setNewCust(c => ({ ...c, business_legal_name: e.target.value }))} className="rounded-xl" data-testid="new-customer-legal-name" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Business Trade Name</label>
                      <Input value={newCust.business_trade_name} onChange={e => setNewCust(c => ({ ...c, business_trade_name: e.target.value }))} className="rounded-xl" data-testid="new-customer-trade-name" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Tax Preference</label>
                    <div className="flex gap-4">
                      {[['taxable', 'Taxable'], ['tax_exempt', 'Tax Exempt']].map(([v, l]) => (
                        <label key={v} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="clinic_tax_pref" checked={newCust.tax_preference === v}
                            onChange={() => setNewCust(c => ({ ...c, tax_preference: v }))} className="accent-[#0B1B3D]" data-testid={`new-tax-pref-${v}`} />
                          <span className="text-sm">{l}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-xl">Cancel</Button>
              <Button onClick={handleCreate} disabled={creating} className="rounded-xl bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="save-customer-btn">{creating ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ─── Customer Profile Dialog ───────────────────────────── */
function CustomerProfileDialog({ customerId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('invoices');
  const [banks, setBanks] = useState([]);
  const [showAdv, setShowAdv] = useState(false);
  const [savingAdv, setSavingAdv] = useState(false);
  const [advForm, setAdvForm] = useState({ amount: '', payment_mode: 'cash', deposit_to: '', payment_date: new Date().toISOString().split('T')[0], reference: '', notes: '' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { billingAPI.getBanks().then(({ data }) => setBanks(data.banks || [])).catch(() => {}); }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await billingAPI.getCustomerProfile(customerId);
        setProfile(data);
      } catch (e) { toast.error('Failed to load profile'); onClose(); }
      finally { setLoading(false); }
    })();
  }, [customerId, onClose, reloadKey]);

  const handleRecordAdvance = async () => {
    if (!advForm.amount || Number(advForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSavingAdv(true);
    try {
      await billingAPI.recordAdvance({
        customer_id: customerId,
        customer_name: profile?.customer?.name || '',
        ...advForm,
        amount: Number(advForm.amount),
      });
      toast.success('Advance recorded');
      setShowAdv(false);
      setAdvForm({ amount: '', payment_mode: 'cash', deposit_to: '', payment_date: new Date().toISOString().split('T')[0], reference: '', notes: '' });
      setReloadKey(k => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to record advance'); }
    finally { setSavingAdv(false); }
  };

  if (loading) return null;
  if (!profile) return null;

  const { customer, invoices, sales_orders, payments, summary } = profile;

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="customer-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0F4C81] text-white flex items-center justify-center font-bold">
              {(customer.name || 'C')[0]}
            </div>
            <div>
              <span>{customer.name}</span>
              <p className="text-sm font-normal text-gray-500">{customer.phone} | {customer.state}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <p className="text-xs text-blue-600">Invoiced</p>
            <p className="text-lg font-bold text-blue-700">₹{fmt(summary.total_invoiced)}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center">
            <p className="text-xs text-amber-600">Sales Orders</p>
            <p className="text-lg font-bold text-amber-700">₹{fmt(summary.total_sales_orders)}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <p className="text-xs text-green-600">Paid</p>
            <p className="text-lg font-bold text-green-700">₹{fmt(summary.total_paid)}</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${summary.balance_due > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
            <p className={`text-xs ${summary.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>Balance</p>
            <p className={`text-lg font-bold ${summary.balance_due > 0 ? 'text-red-700' : 'text-green-700'}`}>₹{fmt(summary.balance_due)}</p>
          </div>
        </div>

        {/* Advance payment */}
        <div className="mt-3">
          {!showAdv ? (
            <Button size="sm" variant="outline" className="rounded-xl gap-1.5" onClick={() => setShowAdv(true)} data-testid="record-advance-btn">
              <Money size={14} /> Record Advance
            </Button>
          ) : (
            <div className="border rounded-xl p-3 space-y-3 bg-gray-50">
              <p className="text-xs font-semibold text-[#0B1B3D]">Record Customer Advance</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div><label className="text-xs text-gray-500">Amount *</label>
                  <Input type="number" value={advForm.amount} onChange={e => setAdvForm(f => ({ ...f, amount: e.target.value }))} className="rounded-lg h-8 text-sm" data-testid="advance-amount" /></div>
                <div><label className="text-xs text-gray-500">Mode</label>
                  <Select value={advForm.payment_mode} onValueChange={v => setAdvForm(f => ({ ...f, payment_mode: v }))}>
                    <SelectTrigger className="rounded-lg h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem></SelectContent>
                  </Select></div>
                <div><label className="text-xs text-gray-500">Deposit To</label>
                  <select value={advForm.deposit_to} onChange={e => setAdvForm(f => ({ ...f, deposit_to: e.target.value }))} className="w-full h-8 border rounded-lg px-2 text-xs" data-testid="advance-deposit-to">
                    <option value="">Select account</option>
                    {banks.map(b => <option key={b.id} value={b.account_name}>{b.account_name}</option>)}
                  </select></div>
                <div><label className="text-xs text-gray-500">Date</label>
                  <Input type="date" value={advForm.payment_date} onChange={e => setAdvForm(f => ({ ...f, payment_date: e.target.value }))} className="rounded-lg h-8 text-xs" /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleRecordAdvance} disabled={savingAdv} className="rounded-xl bg-[#0F4C81] text-white" data-testid="advance-save-btn">{savingAdv ? 'Saving...' : 'Save Advance'}</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAdv(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4 border-b pb-2">
          {['invoices', 'orders', 'payments'].map(t => (
            <Button key={t} variant={activeTab === t ? 'default' : 'outline'} size="sm"
              className={`rounded-xl capitalize ${activeTab === t ? 'bg-[#0F4C81] text-white' : ''}`}
              onClick={() => setActiveTab(t)}>{t === 'orders' ? 'Sales Orders' : t}</Button>
          ))}
        </div>

        {activeTab === 'invoices' && (
          <div className="space-y-2 mt-2">
            {invoices.length === 0 ? <p className="text-gray-400 text-sm py-4 text-center">No invoices</p> :
              invoices.map(inv => (
                <div key={inv.id} className="flex justify-between items-center p-3 border rounded-xl text-sm">
                  <div><p className="font-medium">{inv.invoice_number}</p><p className="text-xs text-gray-400">{inv.date}</p></div>
                  <div className="text-right">
                    <p className="font-semibold">₹{fmt(inv.grand_total)}</p>
                    <Badge variant="outline" className={`text-xs ${invStatusMeta(inv.payment_status).color}`}>{invStatusMeta(inv.payment_status).label}</Badge>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="space-y-2 mt-2">
            {sales_orders.length === 0 ? <p className="text-gray-400 text-sm py-4 text-center">No sales orders</p> :
              sales_orders.map(so => (
                <div key={so.id} className="flex justify-between items-center p-3 border rounded-xl text-sm">
                  <div><p className="font-medium">{so.so_number}</p><p className="text-xs text-gray-400">{so.date}</p></div>
                  <div className="text-right">
                    <p className="font-semibold">₹{fmt(so.grand_total)}</p>
                    <Badge variant="outline" className={`text-xs ${SO_STATUS[so.status]?.color || ''}`}>{SO_STATUS[so.status]?.label || so.status}</Badge>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="space-y-2 mt-2">
            {payments.length === 0 ? <p className="text-gray-400 text-sm py-4 text-center">No payments</p> :
              payments.map(p => (
                <div key={p.id} className="flex justify-between items-center p-3 border rounded-xl text-sm">
                  <div><p className="font-medium">₹{fmt(p.amount)}</p><p className="text-xs text-gray-400">{p.reference_number} | {p.payment_mode}</p></div>
                  <span className="text-xs text-gray-400">{p.payment_date}</span>
                </div>
              ))
            }
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Invoice History Tab ───────────────────────────────── */
function HistoryTab({ clinic, onViewInvoice }) {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: 30 };
      if (search) params.search = search;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (statusFilter) params.status = statusFilter;
      const { data } = await billingAPI.getInvoices(params);
      setInvoices(data.invoices);
      setTotal(data.total);
    } catch (e) { console.error(e); }
  }, [search, fromDate, toDate, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const setQuickRange = (preset) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    if (preset === 'today') { setFromDate(fmt(today)); setToDate(fmt(today)); }
    else if (preset === 'month') { const s = new Date(today.getFullYear(), today.getMonth(), 1); setFromDate(fmt(s)); setToDate(fmt(today)); }
    else if (preset === 'fy') { const fyStart = today.getMonth() >= 3 ? new Date(today.getFullYear(), 3, 1) : new Date(today.getFullYear() - 1, 3, 1); setFromDate(fmt(fyStart)); setToDate(fmt(today)); }
    else { setFromDate(''); setToDate(''); }
    setPage(1);
  };

  return (
    <div className="space-y-4" data-testid="invoice-history">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-3 text-gray-400" />
          <Input data-testid="history-search" placeholder="Search invoices, customers, phones..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 rounded-xl" />
        </div>
        <Input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }} className="rounded-xl w-40" data-testid="history-from-date" />
        <Input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }} className="rounded-xl w-40" data-testid="history-to-date" />
        <Select value={statusFilter || 'all'} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-36 rounded-xl" data-testid="history-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="partial">Partially Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <span className="text-gray-500">Quick range:</span>
        <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => setQuickRange('today')}>Today</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => setQuickRange('month')}>This Month</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => setQuickRange('fy')}>This FY</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => setQuickRange('all')}>All Time</Button>
      </div>
      <p className="text-sm text-gray-500">{total} invoices found</p>
      <div className="space-y-2">
        {invoices.map(inv => (
          <Card key={inv.id} className="border border-gray-100 rounded-xl hover:border-gray-300 cursor-pointer"
            onClick={() => onViewInvoice(inv)} data-testid={`history-invoice-${inv.id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-sm text-[#0B1B3D]">{inv.invoice_number}</p>
                <p className="text-xs text-gray-500">{inv.customer?.name || inv.customer_name || '—'} | {inv.audiologist?.name || '—'}</p>
                <p className="text-xs text-gray-400">{inv.date} | {inv.payment_mode}</p>
                {inv.source_so_number && <p className="text-xs text-blue-500">From: {inv.source_so_number}</p>}
              </div>
              <div className="text-right">
                <p className="font-semibold text-[#0B1B3D]">₹{fmt(inv.grand_total)}</p>
                <Badge variant="outline" className={`text-xs ${invStatusMeta(inv.payment_status).color}`}>
                  {invStatusMeta(inv.payment_status).label}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
        {invoices.length === 0 && <p className="text-center text-gray-400 py-8">No invoices found</p>}
      </div>
      {total > 30 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl">Previous</Button>
          <span className="text-sm text-gray-500 px-3 py-2">Page {page}</span>
          <Button variant="outline" size="sm" disabled={page * 30 >= total} onClick={() => setPage(p => p + 1)} className="rounded-xl">Next</Button>
        </div>
      )}
    </div>
  );
}

/* ─── Invoice Detail Modal ──────────────────────────────── */
function InvoiceDetailModal({ invoice, onClose, canPay = true }) {
  const [downloading, setDownloading] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'cash', payment_date: new Date().toISOString().split('T')[0], notes: '', deposit_to: '' });
  const [payments, setPayments] = useState([]);
  const [payBanks, setPayBanks] = useState([]);
  const [showPayForm, setShowPayForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const payModes = usePaymentModes();

  const loadPayments = useCallback(async () => {
    try {
      const { data } = await billingAPI.getPayments({ invoice_id: invoice.id });
      setPayments(data.payments || []);
    } catch (e) { console.error(e); }
  }, [invoice.id]);

  useEffect(() => { loadPayments(); }, [loadPayments]);
  useEffect(() => { billingAPI.getBanks().then(({ data }) => setPayBanks(data.banks || [])).catch(() => {}); }, []);

  const handlePrint = async () => {
    try {
      const { data } = await billingAPI.getInvoice(invoice.id);
      printInvoiceDocument(data, { payments: data.payments || payments });
    } catch (e) { printInvoiceDocument(invoice, { payments }); }
  };
  const handleDownload = handlePrint;

  const handleRecordPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) return;
    if (!payForm.notes || !payForm.notes.trim()) { toast.error('Remark is required'); return; }
    setSubmitting(true);
    try {
      await billingAPI.recordPayment({ invoice_id: invoice.id }, { ...payForm, amount: Number(payForm.amount) });
      toast.success('Payment recorded');
      setPayForm({ amount: '', payment_mode: 'cash', payment_date: new Date().toISOString().split('T')[0], notes: '', deposit_to: '' });
      setShowPayForm(false);
      loadPayments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const activePayments = payments.filter(p => !['dissociated', 'refunded'].includes(p.status));
  const totalPaid = activePayments.reduce((s, p) => s + p.amount, 0) || invoice.total_paid || (invoice.payment_status === 'paid' ? invoice.grand_total : 0);
  const balance = Math.max(invoice.grand_total - totalPaid, 0);
  // Normalize discount across clinic-created (total_line_discount + additional_discount)
  // and imported/Zoho (total_discount) invoice shapes.
  const totalDiscount = Number(
    invoice.total_discount ?? ((Number(invoice.total_line_discount) || 0) + (Number(invoice.additional_discount) || 0))
  ) || 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="invoice-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-semibold font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">{invoice.invoice_number}</h2>
            <p className="text-sm text-gray-500">{invoice.date} | {invoice.clinic_name}</p>
            {invoice.source_so_number && <p className="text-xs text-blue-500">From SO: {invoice.source_so_number}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} className="rounded-xl gap-1.5" data-testid="print-invoice-btn"><Printer size={16} /> Print</Button>
            <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading} className="rounded-xl gap-1.5" data-testid="download-invoice-btn"><DownloadSimple size={16} /> {downloading ? '...' : 'PDF'}</Button>
            <Button variant="ghost" onClick={onClose} className="text-gray-400">✕</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div><p className="text-xs text-gray-500 mb-1">Customer</p><p className="font-medium">{invoice.customer?.name || invoice.customer_name || '—'}</p><p className="text-gray-500">{invoice.customer?.phone || invoice.customer_phone || ''}</p></div>
          <div><p className="text-xs text-gray-500 mb-1">Audiologist</p><p className="font-medium">{invoice.audiologist?.name || invoice.audiologist_name || invoice.salesperson_name || '—'}</p></div>
        </div>

        <table className="w-full text-sm mb-4">
          <thead><tr className="border-b text-left text-gray-500">
            <th className="pb-2 font-medium">Item</th>
            <th className="pb-2 font-medium text-center">Qty</th>
            <th className="pb-2 font-medium text-right">Rate</th>
            <th className="pb-2 font-medium text-right">Disc.</th>
            <th className="pb-2 font-medium text-center">GST</th>
            <th className="pb-2 font-medium text-right">Amount</th>
          </tr></thead>
          <tbody>
            {invoice.line_items?.map((li, idx) => {
              const disc = Number(li.discount ?? li.discount_amount ?? 0);
              const amt = li.amount ?? li.item_total ?? li.total ?? li.line_amount ?? ((Number(li.rate) || 0) * (Number(li.quantity) || 0) - disc);
              const serial = (Array.isArray(li.serial_numbers) && li.serial_numbers.length) ? li.serial_numbers.join(', ') : (li.serial_number || '');
              return (
                <tr key={idx} className="border-b border-gray-50">
                  <td className="py-2"><p className="font-medium text-[#0B1B3D]">{li.name}</p>{serial && <p className="text-xs text-gray-400">SN: {serial}</p>}</td>
                  <td className="text-center">{li.quantity}</td>
                  <td className="text-right">₹{fmt(li.rate)}</td>
                  <td className="text-right text-orange-600">{disc ? `- ₹${fmt(disc)}` : '—'}</td>
                  <td className="text-center text-xs">{li.tax_percent ? `${li.tax_percent}%` : '—'}</td>
                  <td className="text-right font-medium">₹{fmt(amt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="border-t pt-3 space-y-1 text-sm">
          {(() => {
            const t = invoiceTotals({ ...invoice, additional_discount: 0 });
            const disc = Math.max(totalDiscount, t.discount);
            return (
              <>
                <div className="flex justify-between"><span className="text-gray-500">Sub Total</span><span data-testid="clinic-inv-subtotal">₹{fmt(t.gross)}</span></div>
                {disc > 0 && <div className="flex justify-between text-orange-600"><span>Total Discount</span><span>- ₹{fmt(disc)}</span></div>}
                {t.cgst > 0 && <div className="flex justify-between"><span className="text-gray-500">CGST</span><span>₹{fmt(t.cgst)}</span></div>}
                {t.sgst > 0 && <div className="flex justify-between"><span className="text-gray-500">SGST</span><span>₹{fmt(t.sgst)}</span></div>}
                {t.igst > 0 && <div className="flex justify-between"><span className="text-gray-500">IGST</span><span>₹{fmt(t.igst)}</span></div>}
              </>
            );
          })()}
          <div className="flex justify-between font-semibold text-lg border-t pt-2 text-[#0B1B3D]"><span>Grand Total</span><span>₹{fmt(invoice.grand_total)}</span></div>
        </div>

        {/* Payment section */}
        <div className="mt-4 p-3 bg-gray-50 rounded-xl">
          <div className="flex justify-between text-sm text-green-600"><span>Paid</span><span>₹{fmt(totalPaid)}</span></div>
          <div className="flex justify-between text-sm font-semibold"><span className={balance > 0 ? 'text-red-600' : 'text-green-600'}>Balance</span><span className={balance > 0 ? 'text-red-600' : 'text-green-600'}>₹{fmt(balance)}</span></div>
        </div>

        {payments.length > 0 && (
          <div className="mt-3">
            <h4 className="text-xs font-semibold text-gray-500 mb-1">Payment History</h4>
            {payments.map(p => {
              const isInactive = ['dissociated', 'refunded'].includes(p.status);
              return (
                <div key={p.id} className="flex justify-between items-center text-sm py-1.5 border-b border-gray-50">
                  <span className={isInactive ? 'line-through text-gray-400' : ''}>
                    ₹{fmt(p.amount)} <span className="text-gray-400 text-xs">({p.payment_mode})</span>
                    {p.status === 'dissociated' && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">credited</span>}
                    {p.status === 'refunded' && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">refunded</span>}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-400 mr-1">{p.payment_date}</span>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600"
                      onClick={() => billingAPI.getPaymentReceiptPdf(p.id).catch(() => toast.error('Failed'))}
                      data-testid={`inv-receipt-pdf-${p.id}`}>
                      <Printer size={12} className="mr-1" /> Receipt
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {balance > 0 && canPay && (
          <div className="mt-3">
            {!showPayForm ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setShowPayForm(true)} className="rounded-xl" data-testid="inv-record-payment-btn">
                  <Money size={14} className="mr-1" /> Record Payment
                </Button>
              </div>
            ) : (
              <div className="border rounded-xl p-3 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div><label className="text-xs text-gray-500">Amount *</label>
                    <Input type="number" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} className="rounded-lg h-8 text-sm" data-testid="inv-payment-amount" /></div>
                  <div><label className="text-xs text-gray-500">Mode</label>
                    <Select value={payForm.payment_mode} onValueChange={v => setPayForm(f => ({ ...f, payment_mode: v }))}>
                      <SelectTrigger className="rounded-lg h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{payModes.map(m => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><label className="text-xs text-gray-500">Date</label>
                    <Input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} className="rounded-lg h-8 text-xs" /></div>
                  <div className="col-span-3"><label className="text-xs text-gray-500">Deposit To</label>
                    <select value={payForm.deposit_to} onChange={e => setPayForm(f => ({ ...f, deposit_to: e.target.value }))} className="w-full h-8 border rounded-lg px-2 text-xs" data-testid="inv-payment-deposit-to">
                      <option value="">Select account</option>
                      {payBanks.map(b => <option key={b.id} value={b.account_name}>{b.account_name}</option>)}
                    </select></div>
                  <div className="col-span-3"><label className="text-xs text-gray-500">Remark *</label>
                    <Input value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} placeholder="Payment remark (required)" className="rounded-lg h-8 text-xs" data-testid="inv-payment-remark" /></div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleRecordPayment} disabled={submitting} className="rounded-xl bg-[#0F4C81] text-white" data-testid="inv-confirm-payment-btn">{submitting ? 'Saving...' : 'Save Payment'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowPayForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
